import React, { useState, useEffect, useCallback, useMemo } from "react";
import { PieChart, Pie, Cell, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from "recharts";
import { initializeApp } from "firebase/app";
import { getAuth, onAuthStateChanged, signInWithPopup, signOut as fbSignOut, GoogleAuthProvider } from "firebase/auth";
import { getFirestore, doc, setDoc, onSnapshot } from "firebase/firestore";

const firebaseConfig = {
  apiKey: "AIzaSyAxPbg4I9MCPdS-04iLXJt8fspAooec_ZM",
  authDomain: "budgeting-b3298.firebaseapp.com",
  projectId: "budgeting-b3298",
  storageBucket: "budgeting-b3298.firebasestorage.app",
  messagingSenderId: "375335153432",
  appId: "1:375335153432:web:1a96e9eb7d7f126222386d"
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const googleProvider = new GoogleAuthProvider();

const COLORS = ["#6366f1","#f59e0b","#10b981","#ef4444","#8b5cf6","#ec4899","#14b8a6","#f97316","#06b6d4","#84cc16","#e11d48","#7c3aed"];
const SAVINGS_TYPES = ["General","Travel","Redundancy","Provident Fund","Education","Emergency","Retirement","Home","Custom"];
const EXPENSE_CATEGORIES = ["Grocery","Fuel","Eating Out","Entertainment","Utilities","Internet","Phone","Subscriptions","Transport","Healthcare","Clothing","Personal Care","Gifts","Pet Care","Other"];
const ANNUAL_CATEGORIES = ["Home Insurance","Car Insurance","Property Tax","Road Tax","Life Insurance","Health Insurance","HOA Fees","Maintenance","Other"];
const LOAN_CATEGORIES = ["Mortgage","Car Payment","Student Loan","Personal Loan","Credit Card","Other"];
const LOAN_TYPES = [
  { value: "fixed", label: "Fixed Monthly Payment" },
  { value: "payoff", label: "Balance Payoff by Date" },
];

const CURRENCIES = [
  { code: "EUR", symbol: "€", label: "Euro (€)" },
  { code: "USD", symbol: "$", label: "US Dollar ($)" },
  { code: "GBP", symbol: "£", label: "British Pound (£)" },
  { code: "CHF", symbol: "Fr", label: "Swiss Franc (Fr)" },
  { code: "PLN", symbol: "zł", label: "Polish Zloty (zł)" },
];
const DEFAULT_CURRENCY = "EUR";

const EXCHANGE_RATES_TO_EUR = {
  EUR: 1,
  USD: 0.92,
  GBP: 1.17,
  CHF: 1.05,
  PLN: 0.23,
};

const MONTHS_LIST = ["January","February","March","April","May","June","July","August","September","October","November","December"];

const ACCOUNT_TYPES = [
  { value: "Checking",    label: "Checking",             liquidity: "liquid" },
  { value: "Savings",     label: "Savings",              liquidity: "liquid" },
  { value: "Cash",        label: "Cash",                 liquidity: "liquid" },
  { value: "Brokerage",   label: "Brokerage / Stocks",   liquidity: "semi-liquid" },
  { value: "ISA",         label: "ISA / Index Fund",     liquidity: "semi-liquid" },
  { value: "Crypto",      label: "Crypto",               liquidity: "semi-liquid" },
  { value: "Retirement",  label: "Retirement (IRA/401k)",liquidity: "locked" },
  { value: "Pension",     label: "Pension",              liquidity: "locked" },
  { value: "RealEstate",  label: "Real Estate Equity",   liquidity: "locked" },
  { value: "Other",       label: "Other",                liquidity: "liquid" },
];

const LIQUIDITY_TYPES = [
  { value: "liquid",      label: "Liquid",       desc: "Cash, checking, savings — instantly accessible", color: "#10b981", icon: "💵" },
  { value: "semi-liquid", label: "Semi-liquid",  desc: "Stocks, ISA — accessible in days, value may fluctuate", color: "#f59e0b", icon: "📈" },
  { value: "locked",      label: "Locked",       desc: "Retirement, pension, real estate — restricted or penalty to withdraw", color: "#ef4444", icon: "🔒" },
];

// Short-term debt = categories that can realistically be paid off early with savings
const SHORT_TERM_LOAN_CATEGORIES = ["Credit Card", "Personal Loan", "Student Loan", "Other"];

// rates param uses live user rates; falls back to hardcoded defaults
const toEur = (amount, currency, rates) => {
  const r = rates || EXCHANGE_RATES_TO_EUR;
  return Number(amount || 0) * (r[currency] || 1);
};

const uid = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "EUR", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);
const fmtCur = (n, cur) => new Intl.NumberFormat("en-US", { style: "currency", currency: cur || "EUR", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);
const monthDiff = (d1, d2) => {
  const a = new Date(d1), b = new Date(d2);
  return (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth());
};
const today = () => new Date().toISOString().slice(0, 10);
const toMonth = (d) => new Date(d).toISOString().slice(0, 7);

const getLoanMonthly = (loan) => {
  if (loan.loanType === "payoff") {
    const P = Number(loan.remainingBalance || 0);
    const n = Math.max(1, monthDiff(today(), loan.endDate));
    const annualRate = Number(loan.interestRate || 0);
    if (annualRate > 0) {
      // Amortization formula: M = P·r(1+r)^n / ((1+r)^n - 1)
      const r = annualRate / 100 / 12;
      const factor = Math.pow(1 + r, n);
      return (P * r * factor) / (factor - 1);
    }
    return P / n;
  }
  return Number(loan.amount || 0);
};

const getLoanMonthlyEur = (loan, rates) => {
  const monthly = getLoanMonthly(loan);
  const rate = (rates || EXCHANGE_RATES_TO_EUR)[loan.currency || "EUR"] || 1;
  return monthly * rate;
};

const emptyData = () => ({
  income: [],
  monthlyExpenses: [],
  annualCosts: [],
  loans: [],
  lumpSums: [],
  savings: [],
  actuals: [],
  accounts: [],
  scenarioAdjustments: { expenseMultiplier: 1, savingsMultiplier: 1, incomeMultiplier: 1, startMonth: toMonth(today()), itemOverrides: {} },
  exchangeRates: { ...EXCHANGE_RATES_TO_EUR },
  loanPayoffScenarios: [],
});

/* ── Global Styles ── */
const globalStyles = `
  * { margin: 0; padding: 0; box-sizing: border-box; }
  html, body, #root { background: #0f0f1a; min-height: 100vh; }
  body { overflow-x: hidden; }
  .hide-scrollbar::-webkit-scrollbar { display: none; }
  .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
`;

/* ── Shared Styles ── */
const inputStyle = { width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #334155", background: "#0f172a", color: "#e2e8f0", fontSize: 14, boxSizing: "border-box" };
const labelStyle = { display: "block", color: "#94a3b8", fontSize: 13, marginBottom: 4, marginTop: 12 };
const btnPrimary = { padding: "10px 20px", borderRadius: 8, border: "none", background: "#6366f1", color: "white", fontSize: 14, cursor: "pointer", fontWeight: 600 };
const btnDanger = { padding: "6px 12px", borderRadius: 6, border: "none", background: "#ef4444", color: "white", fontSize: 12, cursor: "pointer" };
const btnOutline = { padding: "8px 16px", borderRadius: 8, border: "1px solid #334155", background: "transparent", color: "#e2e8f0", fontSize: 13, cursor: "pointer" };
const cardStyle = { background: "#1e1e2e", borderRadius: 12, padding: 16, marginBottom: 12, border: "1px solid #2d2d44" };

/* ── Small Reusable Components ── */
const FormField = ({ label, children }) => (
  <div>
    <label style={labelStyle}>{label}</label>
    {children}
  </div>
);

const Select = ({ value, onChange, options }) => (
  <select style={inputStyle} value={value} onChange={(e) => onChange(e.target.value)}>
    {options.map((o) => (
      <option key={typeof o === "object" ? o.value : o} value={typeof o === "object" ? o.value : o}>
        {typeof o === "object" ? o.label : o}
      </option>
    ))}
  </select>
);

const AmountWithCurrency = ({ amount, setAmount, currency, setCurrency, label, rates }) => {
  const r = rates || EXCHANGE_RATES_TO_EUR;
  return (
    <div>
      <label style={labelStyle}>{label || "Amount"}</label>
      <div style={{ display: "flex", gap: 8 }}>
        <input style={{ ...inputStyle, flex: 1 }} type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0" />
        <select style={{ ...inputStyle, width: 100, flex: "none" }} value={currency} onChange={(e) => setCurrency(e.target.value)}>
          {CURRENCIES.map((c) => (
            <option key={c.code} value={c.code}>{c.symbol} {c.code}</option>
          ))}
        </select>
      </div>
      {currency !== "EUR" && amount && (
        <p style={{ color: "#64748b", fontSize: 11, marginTop: 4 }}>
          ≈ {fmt(toEur(Number(amount), currency, r))} EUR (rate: 1 {currency} = {r[currency] || "?"} EUR)
        </p>
      )}
    </div>
  );
};

const Modal = ({ open, onClose, title, children }) => {
  if (!open) return null;
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.5)", padding: 16 }} onClick={onClose}>
      <div style={{ background: "#1e1e2e", borderRadius: 16, padding: 24, width: "100%", maxWidth: 480, maxHeight: "85vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.5)" }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h3 style={{ margin: 0, color: "#e2e8f0", fontSize: 18 }}>{title}</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#94a3b8", fontSize: 22, cursor: "pointer" }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
};

const ItemCard = ({ item, onEdit, onDelete, extra, rates }) => {
  const cur = item.currency || "EUR";
  const r = rates || EXCHANGE_RATES_TO_EUR;
  const isPayoff = item.loanType === "payoff";
  const displayAmount = isPayoff ? getLoanMonthly(item) : Number(item.amount || 0);
  const eurAmount = toEur(displayAmount, cur, r);
  return (
    <div style={cardStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, color: "#e2e8f0", fontSize: 15, marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.name}</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 2 }}>
            {item.category && <span style={{ color: "#6366f1", fontSize: 12 }}>{item.category}</span>}
            {item.interestRate && <span style={{ background: "#f59e0b22", color: "#f59e0b", fontSize: 11, padding: "1px 6px", borderRadius: 4 }}>{item.interestRate}% APR</span>}
          </div>
          {item.type && <div style={{ color: "#8b5cf6", fontSize: 12 }}>{item.type === "Custom" ? item.customType : item.type}</div>}
          {isPayoff && <div style={{ color: "#f59e0b", fontSize: 12 }}>Balance: {fmtCur(item.remainingBalance, cur)} — auto-calculated</div>}
          {extra && <div style={{ color: "#94a3b8", fontSize: 12, marginTop: 2 }}>{extra}</div>}
          {item.notes && <div style={{ color: "#64748b", fontSize: 11, marginTop: 3, fontStyle: "italic" }}>{item.notes}</div>}
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ fontWeight: 700, color: "#e2e8f0", fontSize: 16 }}>
            {fmtCur(displayAmount, cur)}<span style={{ fontSize: 11, color: "#94a3b8" }}>/mo</span>
          </div>
          {cur !== "EUR" && <div style={{ color: "#64748b", fontSize: 11 }}>≈ {fmt(eurAmount)}</div>}
          <div style={{ display: "flex", gap: 6, marginTop: 6, justifyContent: "flex-end" }}>
            <button onClick={() => onEdit(item)} style={{ ...btnOutline, padding: "4px 10px", fontSize: 11 }}>Edit</button>
            <button onClick={() => onDelete(item.id)} style={{ ...btnDanger, padding: "4px 10px", fontSize: 11 }}>Del</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const GenericItemCard = ({ item, onEdit, onDelete, extra, rates }) => {
  const cur = item.currency || "EUR";
  const r = rates || EXCHANGE_RATES_TO_EUR;
  const eurAmount = toEur(Number(item.amount || 0), cur, r);
  return (
    <div style={cardStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, color: "#e2e8f0", fontSize: 15, marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.name}</div>
          {item.category && <div style={{ color: "#6366f1", fontSize: 12 }}>{item.category}</div>}
          {item.type && <div style={{ color: "#8b5cf6", fontSize: 12 }}>{item.type === "Custom" ? item.customType : item.type}</div>}
          {extra && <div style={{ color: "#94a3b8", fontSize: 12, marginTop: 2 }}>{extra}</div>}
          {item.notes && <div style={{ color: "#64748b", fontSize: 11, marginTop: 3, fontStyle: "italic" }}>{item.notes}</div>}
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ fontWeight: 700, color: "#e2e8f0", fontSize: 16 }}>{fmtCur(item.amount, cur)}</div>
          {cur !== "EUR" && <div style={{ color: "#64748b", fontSize: 11 }}>≈ {fmt(eurAmount)}</div>}
          <div style={{ display: "flex", gap: 6, marginTop: 6, justifyContent: "flex-end" }}>
            <button onClick={() => onEdit(item)} style={{ ...btnOutline, padding: "4px 10px", fontSize: 11 }}>Edit</button>
            <button onClick={() => onDelete(item.id)} style={{ ...btnDanger, padding: "4px 10px", fontSize: 11 }}>Del</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ icon, label, value, color, rawDisplay }) => (
  <div style={{ ...cardStyle, textAlign: "center", flex: "1 1 140px", minWidth: 140 }}>
    <div style={{ fontSize: 24 }}>{icon}</div>
    <div style={{ color: "#94a3b8", fontSize: 11, marginTop: 4, marginBottom: 2 }}>{label}</div>
    <div style={{ fontWeight: 700, fontSize: 18, color: color || "#e2e8f0" }}>{rawDisplay !== undefined ? rawDisplay : fmt(value)}</div>
  </div>
);

const errStyle = { color: "#ef4444", fontSize: 11, marginTop: 3 };

/* ── Form Components ── */
const IncomeForm = ({ item, onSave, rates }) => {
  const [name, setName] = useState(item?.name || "");
  const [amount, setAmount] = useState(item?.amount || "");
  const [currency, setCurrency] = useState(item?.currency || DEFAULT_CURRENCY);
  const [notes, setNotes] = useState(item?.notes || "");
  const [errors, setErrors] = useState({});
  const validate = () => {
    const e = {};
    if (!name.trim()) e.name = "Name is required";
    if (!amount || Number(amount) <= 0) e.amount = "Amount must be greater than 0";
    return e;
  };
  const handleSave = () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    onSave({ ...item, name, amount: Number(amount), currency, notes });
  };
  return (
    <div>
      <FormField label="Source Name">
        <input style={{ ...inputStyle, borderColor: errors.name ? "#ef4444" : "#334155" }} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Salary" />
        {errors.name && <p style={errStyle}>{errors.name}</p>}
      </FormField>
      <AmountWithCurrency amount={amount} setAmount={setAmount} currency={currency} setCurrency={setCurrency} label="Monthly Amount" rates={rates} />
      {errors.amount && <p style={errStyle}>{errors.amount}</p>}
      <FormField label="Notes (optional)"><textarea style={{ ...inputStyle, minHeight: 60, resize: "vertical" }} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any notes..." /></FormField>
      <div style={{ marginTop: 20 }}><button style={btnPrimary} onClick={handleSave}>{item?.id ? "Update" : "Add"}</button></div>
    </div>
  );
};

const ExpenseForm = ({ item, onSave, rates }) => {
  const [name, setName] = useState(item?.name || "");
  const [amount, setAmount] = useState(item?.amount || "");
  const [currency, setCurrency] = useState(item?.currency || DEFAULT_CURRENCY);
  const [category, setCategory] = useState(item?.category || EXPENSE_CATEGORIES[0]);
  const [notes, setNotes] = useState(item?.notes || "");
  const [errors, setErrors] = useState({});
  const handleSave = () => {
    const e = {};
    if (!name.trim()) e.name = "Name is required";
    if (!amount || Number(amount) <= 0) e.amount = "Amount must be greater than 0";
    if (Object.keys(e).length) { setErrors(e); return; }
    onSave({ ...item, name, amount: Number(amount), currency, category, notes });
  };
  return (
    <div>
      <FormField label="Description">
        <input style={{ ...inputStyle, borderColor: errors.name ? "#ef4444" : "#334155" }} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Weekly groceries" />
        {errors.name && <p style={errStyle}>{errors.name}</p>}
      </FormField>
      <FormField label="Category"><Select value={category} onChange={setCategory} options={EXPENSE_CATEGORIES} /></FormField>
      <AmountWithCurrency amount={amount} setAmount={setAmount} currency={currency} setCurrency={setCurrency} label="Monthly Amount" rates={rates} />
      {errors.amount && <p style={errStyle}>{errors.amount}</p>}
      <FormField label="Notes (optional)"><textarea style={{ ...inputStyle, minHeight: 60, resize: "vertical" }} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any notes..." /></FormField>
      <div style={{ marginTop: 20 }}><button style={btnPrimary} onClick={handleSave}>{item?.id ? "Update" : "Add"}</button></div>
    </div>
  );
};

const AnnualForm = ({ item, onSave, rates }) => {
  const [name, setName] = useState(item?.name || "");
  const [amount, setAmount] = useState(item?.amount || "");
  const [currency, setCurrency] = useState(item?.currency || DEFAULT_CURRENCY);
  const [category, setCategory] = useState(item?.category || ANNUAL_CATEGORIES[0]);
  const [dueMonth, setDueMonth] = useState(item?.dueMonth || "");
  const [notes, setNotes] = useState(item?.notes || "");
  const [errors, setErrors] = useState({});
  const r = rates || EXCHANGE_RATES_TO_EUR;
  const eurAmount = toEur(Number(amount || 0), currency, r);
  const handleSave = () => {
    const e = {};
    if (!name.trim()) e.name = "Name is required";
    if (!amount || Number(amount) <= 0) e.amount = "Amount must be greater than 0";
    if (Object.keys(e).length) { setErrors(e); return; }
    onSave({ ...item, name, amount: Number(amount), currency, category, dueMonth: dueMonth ? Number(dueMonth) : null, notes });
  };
  return (
    <div>
      <FormField label="Description">
        <input style={{ ...inputStyle, borderColor: errors.name ? "#ef4444" : "#334155" }} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Home Insurance" />
        {errors.name && <p style={errStyle}>{errors.name}</p>}
      </FormField>
      <FormField label="Category"><Select value={category} onChange={setCategory} options={ANNUAL_CATEGORIES} /></FormField>
      <AmountWithCurrency amount={amount} setAmount={setAmount} currency={currency} setCurrency={setCurrency} label="Annual Amount" rates={rates} />
      {errors.amount && <p style={errStyle}>{errors.amount}</p>}
      <p style={{ color: "#94a3b8", fontSize: 12, marginTop: 4 }}>Monthly set-aside: {fmt(eurAmount / 12)} EUR</p>
      <FormField label="Month it's due (optional)">
        <select style={inputStyle} value={dueMonth} onChange={(e) => setDueMonth(e.target.value)}>
          <option value="">— Spread evenly across year —</option>
          {MONTHS_LIST.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
        </select>
      </FormField>
      <FormField label="Notes (optional)"><textarea style={{ ...inputStyle, minHeight: 60, resize: "vertical" }} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any notes..." /></FormField>
      <div style={{ marginTop: 20 }}><button style={btnPrimary} onClick={handleSave}>{item?.id ? "Update" : "Add"}</button></div>
    </div>
  );
};

const LoanForm = ({ item, onSave, rates }) => {
  const [name, setName] = useState(item?.name || "");
  const [loanType, setLoanType] = useState(item?.loanType || "fixed");
  const [amount, setAmount] = useState(item?.amount || "");
  const [remainingBalance, setRemainingBalance] = useState(item?.remainingBalance || "");
  const [currency, setCurrency] = useState(item?.currency || DEFAULT_CURRENCY);
  const [category, setCategory] = useState(item?.category || LOAN_CATEGORIES[0]);
  const [endDate, setEndDate] = useState(item?.endDate || "");
  const [interestRate, setInterestRate] = useState(item?.interestRate || "");
  const [notes, setNotes] = useState(item?.notes || "");
  const [errors, setErrors] = useState({});

  const r = rates || EXCHANGE_RATES_TO_EUR;
  const months = endDate ? Math.max(1, monthDiff(today(), endDate)) : 0;
  const calcMonthly = (() => {
    if (months <= 0) return 0;
    const P = Number(remainingBalance || 0);
    const rate = Number(interestRate || 0);
    if (rate > 0) {
      const mr = rate / 100 / 12;
      const factor = Math.pow(1 + mr, months);
      return (P * mr * factor) / (factor - 1);
    }
    return P / months;
  })();
  const eurCalcMonthly = toEur(calcMonthly, currency, r);

  const handleSave = () => {
    const e = {};
    if (!name.trim()) e.name = "Name is required";
    if (loanType === "fixed" && (!amount || Number(amount) <= 0)) e.amount = "Monthly payment must be greater than 0";
    if (loanType === "payoff" && (!remainingBalance || Number(remainingBalance) <= 0)) e.balance = "Remaining balance must be greater than 0";
    if (loanType === "payoff" && !endDate) e.endDate = "Payoff date is required";
    if (Object.keys(e).length) { setErrors(e); return; }
    onSave({
      ...item, name, loanType, currency, category, endDate, notes,
      interestRate: interestRate ? Number(interestRate) : null,
      amount: loanType === "fixed" ? Number(amount) : 0,
      remainingBalance: loanType === "payoff" ? Number(remainingBalance) : 0,
    });
  };

  return (
    <div>
      <FormField label="Loan Name">
        <input style={{ ...inputStyle, borderColor: errors.name ? "#ef4444" : "#334155" }} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Car loan" />
        {errors.name && <p style={errStyle}>{errors.name}</p>}
      </FormField>
      <FormField label="Category"><Select value={category} onChange={setCategory} options={LOAN_CATEGORIES} /></FormField>
      <FormField label="Loan Type"><Select value={loanType} onChange={setLoanType} options={LOAN_TYPES} /></FormField>
      <FormField label="Annual Interest Rate % (optional)">
        <input style={inputStyle} type="number" min="0" max="100" step="0.01" placeholder="e.g. 5.5" value={interestRate} onChange={(e) => setInterestRate(e.target.value)} />
      </FormField>

      {loanType === "fixed" ? (
        <>
          <AmountWithCurrency amount={amount} setAmount={setAmount} currency={currency} setCurrency={setCurrency} label="Monthly Payment" rates={rates} />
          {errors.amount && <p style={errStyle}>{errors.amount}</p>}
          <FormField label="End Date (optional)"><input style={inputStyle} type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} /></FormField>
        </>
      ) : (
        <>
          <AmountWithCurrency amount={remainingBalance} setAmount={setRemainingBalance} currency={currency} setCurrency={setCurrency} label="Remaining Balance" rates={rates} />
          {errors.balance && <p style={errStyle}>{errors.balance}</p>}
          <FormField label="Must Pay Off By">
            <input style={{ ...inputStyle, borderColor: errors.endDate ? "#ef4444" : "#334155" }} type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            {errors.endDate && <p style={errStyle}>{errors.endDate}</p>}
          </FormField>
          {months > 0 && (
            <div style={{ ...cardStyle, marginTop: 12, background: "#0f172a" }}>
              <div style={{ color: "#10b981", fontSize: 14, fontWeight: 600 }}>
                → {fmtCur(calcMonthly, currency)}/month for {months} months
              </div>
              {currency !== "EUR" && (
                <div style={{ color: "#64748b", fontSize: 12, marginTop: 2 }}>≈ {fmt(eurCalcMonthly)}/month EUR</div>
              )}
              {interestRate && Number(interestRate) > 0
                ? <p style={{ color: "#f59e0b", fontSize: 11, marginTop: 4 }}>Includes {interestRate}% APR interest. Update the remaining balance anytime to recalculate.</p>
                : <p style={{ color: "#94a3b8", fontSize: 11, marginTop: 4 }}>No interest rate set — simple balance ÷ months. Add a rate above for accurate amortization.</p>
              }
            </div>
          )}
        </>
      )}
      <FormField label="Notes (optional)"><textarea style={{ ...inputStyle, minHeight: 60, resize: "vertical" }} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any notes..." /></FormField>
      <div style={{ marginTop: 20 }}><button style={btnPrimary} onClick={handleSave}>{item?.id ? "Update" : "Add"}</button></div>
    </div>
  );
};

const LumpSumForm = ({ item, onSave, rates }) => {
  const [name, setName] = useState(item?.name || "");
  const [amount, setAmount] = useState(item?.amount || "");
  const [currency, setCurrency] = useState(item?.currency || DEFAULT_CURRENCY);
  const [dueDate, setDueDate] = useState(item?.dueDate || "");
  const [notes, setNotes] = useState(item?.notes || "");
  const [errors, setErrors] = useState({});
  const r = rates || EXCHANGE_RATES_TO_EUR;
  const months = dueDate ? Math.max(1, monthDiff(today(), dueDate)) : 0;
  const eurAmount = toEur(Number(amount || 0), currency, r);
  const handleSave = () => {
    const e = {};
    if (!name.trim()) e.name = "Name is required";
    if (!amount || Number(amount) <= 0) e.amount = "Amount must be greater than 0";
    if (!dueDate) e.dueDate = "Due date is required";
    if (Object.keys(e).length) { setErrors(e); return; }
    onSave({ ...item, name, amount: Number(amount), currency, dueDate, notes });
  };
  return (
    <div>
      <FormField label="Description">
        <input style={{ ...inputStyle, borderColor: errors.name ? "#ef4444" : "#334155" }} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Vacation fund" />
        {errors.name && <p style={errStyle}>{errors.name}</p>}
      </FormField>
      <AmountWithCurrency amount={amount} setAmount={setAmount} currency={currency} setCurrency={setCurrency} label="Total Amount" rates={rates} />
      {errors.amount && <p style={errStyle}>{errors.amount}</p>}
      <FormField label="Pay Off By">
        <input style={{ ...inputStyle, borderColor: errors.dueDate ? "#ef4444" : "#334155" }} type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
        {errors.dueDate && <p style={errStyle}>{errors.dueDate}</p>}
      </FormField>
      {months > 0 && <p style={{ color: "#10b981", fontSize: 13, marginTop: 8 }}>→ Save {fmt(eurAmount / months)} EUR/month for {months} months</p>}
      <FormField label="Notes (optional)"><textarea style={{ ...inputStyle, minHeight: 60, resize: "vertical" }} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any notes..." /></FormField>
      <div style={{ marginTop: 20 }}><button style={btnPrimary} onClick={handleSave}>{item?.id ? "Update" : "Add"}</button></div>
    </div>
  );
};

const SavingsForm = ({ item, onSave, rates }) => {
  const [name, setName] = useState(item?.name || "");
  const [amount, setAmount] = useState(item?.amount || "");
  const [currency, setCurrency] = useState(item?.currency || DEFAULT_CURRENCY);
  const [type, setType] = useState(item?.type || SAVINGS_TYPES[0]);
  const [customType, setCustomType] = useState(item?.customType || "");
  const [notes, setNotes] = useState(item?.notes || "");
  const [errors, setErrors] = useState({});
  const handleSave = () => {
    const e = {};
    if (!name.trim()) e.name = "Name is required";
    if (!amount || Number(amount) <= 0) e.amount = "Amount must be greater than 0";
    if (Object.keys(e).length) { setErrors(e); return; }
    onSave({ ...item, name, amount: Number(amount), currency, type, customType: type === "Custom" ? customType : "", notes });
  };
  return (
    <div>
      <FormField label="Savings Name">
        <input style={{ ...inputStyle, borderColor: errors.name ? "#ef4444" : "#334155" }} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Holiday fund" />
        {errors.name && <p style={errStyle}>{errors.name}</p>}
      </FormField>
      <FormField label="Type"><Select value={type} onChange={setType} options={SAVINGS_TYPES} /></FormField>
      {type === "Custom" && <FormField label="Custom Type Name"><input style={inputStyle} value={customType} onChange={(e) => setCustomType(e.target.value)} placeholder="Type name" /></FormField>}
      <AmountWithCurrency amount={amount} setAmount={setAmount} currency={currency} setCurrency={setCurrency} label="Monthly Amount" rates={rates} />
      {errors.amount && <p style={errStyle}>{errors.amount}</p>}
      <p style={{ color: "#94a3b8", fontSize: 12, marginTop: 8 }}>💡 This money is tracked as savings, not spending — it counts towards your savings bucket.</p>
      <FormField label="Notes (optional)"><textarea style={{ ...inputStyle, minHeight: 60, resize: "vertical" }} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any notes..." /></FormField>
      <div style={{ marginTop: 20 }}><button style={btnPrimary} onClick={handleSave}>{item?.id ? "Update" : "Add"}</button></div>
    </div>
  );
};

const AccountForm = ({ item, onSave, rates }) => {
  const defaultType = ACCOUNT_TYPES[0];
  const [name, setName] = useState(item?.name || "");
  const [institution, setInstitution] = useState(item?.institution || "");
  const [accountType, setAccountType] = useState(item?.accountType || defaultType.value);
  const [balance, setBalance] = useState(item?.balance || "");
  const [currency, setCurrency] = useState(item?.currency || DEFAULT_CURRENCY);
  const [notes, setNotes] = useState(item?.notes || "");
  const [errors, setErrors] = useState({});

  // Auto-suggest liquidity from account type, but let user override
  const suggestedLiquidity = ACCOUNT_TYPES.find(t => t.value === accountType)?.liquidity || "liquid";
  const [liquidity, setLiquidity] = useState(item?.liquidity || suggestedLiquidity);

  // When accountType changes, update liquidity suggestion only if user hasn't manually changed it
  const [liquidityManuallySet, setLiquidityManuallySet] = useState(!!item?.liquidity);
  const handleTypeChange = (val) => {
    setAccountType(val);
    if (!liquidityManuallySet) {
      setLiquidity(ACCOUNT_TYPES.find(t => t.value === val)?.liquidity || "liquid");
    }
  };

  const handleSave = () => {
    const e = {};
    if (!name.trim()) e.name = "Account name is required";
    if (!balance || Number(balance) < 0) e.balance = "Balance must be 0 or more";
    if (Object.keys(e).length) { setErrors(e); return; }
    onSave({ ...item, name, institution, accountType, balance: Number(balance), currency, liquidity, notes });
  };

  return (
    <div>
      <FormField label="Account Name">
        <input style={{ ...inputStyle, borderColor: errors.name ? "#ef4444" : "#334155" }}
          value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Sapphire Checking" />
        {errors.name && <p style={errStyle}>{errors.name}</p>}
      </FormField>
      <FormField label="Institution (optional)">
        <input style={inputStyle} value={institution} onChange={e => setInstitution(e.target.value)} placeholder="e.g. Chase, Fidelity" />
      </FormField>
      <FormField label="Account Type">
        <Select value={accountType} onChange={handleTypeChange} options={ACCOUNT_TYPES.map(t => ({ value: t.value, label: t.label }))} />
      </FormField>
      <FormField label="Liquidity">
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}>
          {LIQUIDITY_TYPES.map(l => (
            <label key={l.value} style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer", padding: "10px 12px", borderRadius: 8, border: "1px solid " + (liquidity === l.value ? l.color : "#334155"), background: liquidity === l.value ? l.color + "18" : "transparent" }}
              onClick={() => { setLiquidity(l.value); setLiquidityManuallySet(true); }}>
              <span style={{ fontSize: 18, marginTop: 1 }}>{l.icon}</span>
              <div>
                <div style={{ color: liquidity === l.value ? l.color : "#e2e8f0", fontWeight: 600, fontSize: 13 }}>{l.label}</div>
                <div style={{ color: "#64748b", fontSize: 11, marginTop: 2 }}>{l.desc}</div>
              </div>
            </label>
          ))}
        </div>
      </FormField>
      <AmountWithCurrency amount={balance} setAmount={setBalance} currency={currency} setCurrency={setCurrency} label="Current Balance" rates={rates} />
      {errors.balance && <p style={errStyle}>{errors.balance}</p>}
      <FormField label="Notes (optional)">
        <textarea style={{ ...inputStyle, minHeight: 60, resize: "vertical" }} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any notes..." />
      </FormField>
      <div style={{ marginTop: 20 }}><button style={btnPrimary} onClick={handleSave}>{item?.id ? "Update" : "Add"}</button></div>
    </div>
  );
};

/* ── MAIN APP ── */
export default function BudgetApp() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [data, setData] = useState(emptyData());
  const [tab, setTab] = useState("dashboard");
  const [modal, setModal] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [ratesModal, setRatesModal] = useState(false);
  const [activePayoffScenarioId, setActivePayoffScenarioId] = useState(null);

  /* Inject global styles */
  useEffect(() => {
    const id = "budget-global-styles";
    if (!document.getElementById(id)) {
      const style = document.createElement("style");
      style.id = id;
      style.textContent = globalStyles;
      document.head.appendChild(style);
    }
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthLoading(false);
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (!user) return;
    const unsub = onSnapshot(doc(db, "budgets", user.uid), (snap) => {
      if (snap.exists()) setData({ ...emptyData(), ...snap.data() });
    });
    return unsub;
  }, [user]);

  const save = useCallback(async (newData) => {
    setData(newData);
    if (user) {
      setSaving(true);
      try { await setDoc(doc(db, "budgets", user.uid), newData); }
      catch (e) { console.error(e); }
      setSaving(false);
    }
  }, [user]);

  const signIn = async () => {
    try { await signInWithPopup(auth, googleProvider); }
    catch (e) { console.error(e); }
  };

  const handleSignOut = async () => {
    await fbSignOut(auth);
    setData(emptyData());
  };

  const addItem = (key, item) => { const d = { ...data }; d[key] = [...d[key], { ...item, id: uid() }]; save(d); setModal(null); setEditItem(null); };
  const updateItem = (key, item) => { const d = { ...data }; d[key] = d[key].map((i) => (i.id === item.id ? item : i)); save(d); setModal(null); setEditItem(null); };
  const deleteItem = (key, id) => {
    if (!window.confirm("Delete this item? This cannot be undone.")) return;
    const d = { ...data }; d[key] = d[key].filter((i) => i.id !== id); save(d);
  };

  /* ── Calculations (all in EUR) ── */
  const calcs = useMemo(() => {
    const now = new Date();
    const curMonth = toMonth(today());
    const adj = data.scenarioAdjustments || { expenseMultiplier: 1, savingsMultiplier: 1, incomeMultiplier: 1, startMonth: curMonth, itemOverrides: {} };
    const r = data.exchangeRates || EXCHANGE_RATES_TO_EUR;
    const eur = (amt, cur) => Number(amt || 0) * (r[cur] || 1);
    const itemOverrides = adj.itemOverrides || {};

    const totalIncome = data.income.reduce((s, i) => s + eur(i.amount, i.currency || "EUR"), 0);
    const totalMonthly = data.monthlyExpenses.reduce((s, i) => {
      const amt = itemOverrides[i.id] !== undefined ? Number(itemOverrides[i.id]) : eur(i.amount, i.currency || "EUR");
      return s + amt;
    }, 0);
    const totalAnnualMonthly = data.annualCosts.reduce((s, i) => s + eur(i.amount, i.currency || "EUR") / 12, 0);
    const totalLoans = data.loans.reduce((s, i) => {
      if (i.endDate && new Date(i.endDate) < now) return s;
      return s + getLoanMonthlyEur(i, r);
    }, 0);
    const totalLumpMonthly = data.lumpSums.reduce((s, i) => {
      const months = Math.max(1, monthDiff(curMonth + "-01", i.dueDate));
      if (new Date(i.dueDate) < now) return s;
      return s + eur(i.amount, i.currency || "EUR") / months;
    }, 0);
    const totalSavings = data.savings.reduce((s, i) => {
      const amt = itemOverrides[i.id] !== undefined ? Number(itemOverrides[i.id]) : eur(i.amount, i.currency || "EUR");
      return s + amt;
    }, 0);

    // Total debt: sum of remaining balances (payoff loans) + estimated balances (fixed loans with endDate)
    const totalDebt = data.loans.reduce((s, i) => {
      if (i.endDate && new Date(i.endDate) < now) return s;
      if (i.loanType === "payoff") return s + eur(i.remainingBalance, i.currency || "EUR");
      if (i.endDate) {
        const mLeft = Math.max(0, monthDiff(today(), i.endDate));
        return s + eur(i.amount, i.currency || "EUR") * mLeft;
      }
      return s;
    }, 0);

    const totalOut = totalMonthly + totalAnnualMonthly + totalLoans + totalLumpMonthly + totalSavings;
    const netMonthly = totalIncome - totalOut;

    // Build projection months (up to 60; callers can slice)
    const months = [];
    let cumSavings = 0;
    for (let m = 0; m < 60; m++) {
      const d = new Date(now.getFullYear(), now.getMonth() + m, 1);
      const mStr = d.toISOString().slice(0, 7);
      const label = d.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
      const applyAdj = mStr >= adj.startMonth;

      let mIncome = totalIncome * (applyAdj ? (adj.incomeMultiplier || 1) : 1);
      let mExpenses = data.monthlyExpenses.reduce((s, i) => {
        const base = itemOverrides[i.id] !== undefined ? Number(itemOverrides[i.id]) : eur(i.amount, i.currency || "EUR");
        return s + base * (applyAdj && itemOverrides[i.id] === undefined ? adj.expenseMultiplier : 1);
      }, 0);
      let mAnnual = totalAnnualMonthly;
      let mLoans = data.loans.reduce((s, i) => {
        if (i.endDate && new Date(i.endDate) < d) return s;
        if (i.loanType === "payoff") {
          const totalMonths = Math.max(1, monthDiff(curMonth + "-01", i.endDate));
          const bal = eur(i.remainingBalance, i.currency || "EUR");
          return s + bal / totalMonths;
        }
        return s + eur(i.amount, i.currency || "EUR");
      }, 0);
      let mLump = data.lumpSums.reduce((s, i) => {
        const due = new Date(i.dueDate);
        if (due < d) return s;
        const totalMonths = Math.max(1, monthDiff(curMonth + "-01", i.dueDate));
        return s + eur(i.amount, i.currency || "EUR") / totalMonths;
      }, 0);
      let mSav = data.savings.reduce((s, i) => {
        const base = itemOverrides[i.id] !== undefined ? Number(itemOverrides[i.id]) : eur(i.amount, i.currency || "EUR");
        return s + base * (applyAdj && itemOverrides[i.id] === undefined ? adj.savingsMultiplier : 1);
      }, 0);

      const net = mIncome - mExpenses - mAnnual - mLoans - mLump - mSav;
      cumSavings += mSav + Math.max(0, net);

      months.push({
        label, month: mStr, income: mIncome, expenses: mExpenses,
        annual: mAnnual, loans: mLoans, lumpSums: mLump, savings: mSav,
        net, cumSavings: Math.round(cumSavings)
      });
    }

    const pieData = [
      { name: "Expenses", value: Math.round(totalMonthly) },
      { name: "Annual Costs", value: Math.round(totalAnnualMonthly) },
      { name: "Loans", value: Math.round(totalLoans) },
      { name: "Lump Sums", value: Math.round(totalLumpMonthly) },
      { name: "Savings", value: Math.round(totalSavings) },
    ].filter((d) => d.value > 0);

    const savingsByType = {};
    data.savings.forEach((s) => {
      const key = s.type === "Custom" ? (s.customType || "Custom") : s.type;
      savingsByType[key] = (savingsByType[key] || 0) + eur(s.amount, s.currency || "EUR");
    });
    const savingsPie = Object.entries(savingsByType).map(([name, value]) => ({ name, value: Math.round(value) }));

    const expByCategory = {};
    data.monthlyExpenses.forEach((e) => {
      expByCategory[e.category] = (expByCategory[e.category] || 0) + eur(e.amount, e.currency || "EUR");
    });
    const expPie = Object.entries(expByCategory).map(([name, value]) => ({ name, value: Math.round(value) }));

    return { totalIncome, totalMonthly, totalAnnualMonthly, totalLoans, totalLumpMonthly, totalSavings, totalOut, netMonthly, totalDebt, months, pieData, savingsPie, expPie };
  }, [data]);

  /* ── Tabs Config ── */
  const tabs = [
    { id: "dashboard", icon: "📊", label: "Dashboard" },
    { id: "income", icon: "💵", label: "Income" },
    { id: "expenses", icon: "🛒", label: "Expenses" },
    { id: "annual", icon: "📅", label: "Annual" },
    { id: "loans", icon: "🏦", label: "Loans" },
    { id: "lumpsums", icon: "🎯", label: "Lump Sums" },
    { id: "savings", icon: "🐷", label: "Savings" },
    { id: "projections", icon: "📈", label: "Projections" },
    { id: "scenario", icon: "🔮", label: "Scenario" },
    { id: "loanpayoff", icon: "⚡", label: "Loan Payoff" },
    { id: "actuals", icon: "🧾", label: "Actuals" },
    { id: "accounts", icon: "💼", label: "Accounts" },
  ];

  /* ── Exchange Rate Editor ── */
  const ExchangeRateEditor = () => {
    const r = { ...EXCHANGE_RATES_TO_EUR, ...(data.exchangeRates || {}) };
    const updateRate = (code, val) => {
      const newRates = { ...r, [code]: Number(val) };
      save({ ...data, exchangeRates: newRates });
    };
    return (
      <div>
        <p style={{ color: "#94a3b8", fontSize: 13, marginBottom: 16, lineHeight: 1.5 }}>
          Set how much 1 unit of each currency equals in EUR. These rates are used to convert all amounts to EUR for reports and charts.
        </p>
        {CURRENCIES.filter(c => c.code !== "EUR").map(c => (
          <FormField key={c.code} label={`1 ${c.code} = ? EUR (${c.label})`}>
            <input style={inputStyle} type="number" step="0.001" value={r[c.code] ?? ""} onChange={(e) => updateRate(c.code, e.target.value)} />
          </FormField>
        ))}
        <p style={{ color: "#64748b", fontSize: 11, marginTop: 12 }}>EUR is always 1. Update rates manually when needed.</p>
      </div>
    );
  };

  const liveRates = { ...EXCHANGE_RATES_TO_EUR, ...(data.exchangeRates || {}) };

  /* ── CSV Export ── */
  const exportCSV = () => {
    const r = liveRates;
    const eur = (amt, cur) => toEur(amt, cur, r);
    const rows = ["Budget Planner Export — " + new Date().toLocaleDateString(), ""];

    const section = (title, headers, items, rowFn) => {
      rows.push(title);
      rows.push(headers.join(","));
      items.forEach(i => rows.push(rowFn(i).map(v => `"${v}"`).join(",")));
      rows.push("");
    };

    section("INCOME", ["Name","Amount","Currency","EUR equiv"],
      data.income, i => [i.name, i.amount, i.currency || "EUR", eur(i.amount, i.currency || "EUR").toFixed(2)]);
    section("MONTHLY EXPENSES", ["Name","Category","Amount","Currency","EUR equiv"],
      data.monthlyExpenses, i => [i.name, i.category || "", i.amount, i.currency || "EUR", eur(i.amount, i.currency || "EUR").toFixed(2)]);
    section("ANNUAL COSTS", ["Name","Category","Annual Amount","Currency","Monthly EUR"],
      data.annualCosts, i => [i.name, i.category || "", i.amount, i.currency || "EUR", (eur(i.amount, i.currency || "EUR") / 12).toFixed(2)]);
    section("LOANS", ["Name","Category","Type","Monthly EUR","Interest Rate","Balance"],
      data.loans, i => [i.name, i.category || "", i.loanType, getLoanMonthlyEur(i, r).toFixed(2), i.interestRate || "", i.remainingBalance || ""]);
    section("SAVINGS", ["Name","Type","Monthly Amount","Currency","EUR equiv"],
      data.savings, i => [i.name, i.type || "", i.amount, i.currency || "EUR", eur(i.amount, i.currency || "EUR").toFixed(2)]);

    rows.push("SUMMARY");
    rows.push(`Total Monthly Income,${calcs.totalIncome.toFixed(2)}`);
    rows.push(`Total Monthly Outgoing,${calcs.totalOut.toFixed(2)}`);
    rows.push(`Net Monthly,${calcs.netMonthly.toFixed(2)}`);
    rows.push(`Total Debt Estimate,${calcs.totalDebt.toFixed(2)}`);

    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "budget-export.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  /* ── List Renderers ── */
  const renderList = (key, FormComponent, formTitle, extraFn, CardComp) => {
    const items = data[key] || [];
    const Card = CardComp || GenericItemCard;
    return (
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
          <h2 style={{ margin: 0, color: "#e2e8f0", fontSize: 20 }}>{formTitle}</h2>
          <button style={btnPrimary} onClick={() => { setEditItem(null); setModal(key); }}>+ Add</button>
        </div>
        {items.length === 0 && <div style={{ ...cardStyle, textAlign: "center", color: "#64748b", padding: 32 }}>No items yet. Click "+ Add" to get started.</div>}
        {items.map((i) => (
          <Card key={i.id} item={i} extra={extraFn?.(i)} rates={liveRates}
            onEdit={(it) => { setEditItem(it); setModal(key); }}
            onDelete={(id) => deleteItem(key, id)} />
        ))}
        <Modal open={modal === key} onClose={() => { setModal(null); setEditItem(null); }} title={editItem ? `Edit ${formTitle}` : `Add ${formTitle}`}>
          <FormComponent item={editItem} rates={liveRates} onSave={(item) => editItem?.id ? updateItem(key, item) : addItem(key, item)} />
        </Modal>
      </div>
    );
  };

  /* ── Dashboard ── */
  const Dashboard = () => {
    const savingsRate = calcs.totalIncome > 0 ? ((calcs.totalSavings / calcs.totalIncome) * 100).toFixed(1) : "0.0";
    const tooltipStyle = { background: "#1e1e2e", border: "1px solid #334155", borderRadius: 8 };
    const legendStyle = { color: "#94a3b8", fontSize: 12 };
    return (
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, flexWrap: "wrap", gap: 8 }}>
          <h2 style={{ margin: 0, color: "#e2e8f0", fontSize: 20 }}>📊 Dashboard Overview</h2>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button style={{ ...btnOutline, fontSize: 12 }} onClick={exportCSV}>⬇ Export CSV</button>
            <button style={{ ...btnOutline, fontSize: 12 }} onClick={() => setRatesModal(true)}>💱 Exchange Rates</button>
          </div>
        </div>
        <p style={{ color: "#64748b", fontSize: 12, marginBottom: 12 }}>All values shown in EUR. Non-EUR items are auto-converted.</p>

        {/* Quick-add buttons */}
        <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
          {[
            { label: "+ Income", tab: "income", key: "income" },
            { label: "+ Expense", tab: "expenses", key: "monthlyExpenses" },
            { label: "+ Loan", tab: "loans", key: "loans" },
            { label: "+ Savings", tab: "savings", key: "savings" },
          ].map(({ label, tab: t, key }) => (
            <button key={t} style={{ ...btnOutline, fontSize: 12, padding: "6px 12px" }}
              onClick={() => { setEditItem(null); setTab(t); setModal(key); }}>{label}</button>
          ))}
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 24 }}>
          <StatCard icon="💵" label="Total Income" value={calcs.totalIncome} color="#10b981" />
          <StatCard icon="📤" label="Total Outgoing" value={calcs.totalOut} color="#ef4444" />
          <StatCard icon="💰" label="Net Monthly" value={calcs.netMonthly} color={calcs.netMonthly >= 0 ? "#10b981" : "#ef4444"} />
          <StatCard icon="🐷" label="Monthly Savings" value={calcs.totalSavings} color="#8b5cf6" />
          <StatCard icon="📊" label="Savings Rate" rawDisplay={`${savingsRate}%`} color="#06b6d4" />
          {calcs.totalDebt > 0 && <StatCard icon="🏦" label="Total Debt Est." value={calcs.totalDebt} color="#f97316" />}
          {(() => {
            const totalAssets = (data.accounts || []).reduce((s, a) => s + toEur(a.balance, a.currency || "EUR", liveRates), 0);
            if (totalAssets === 0) return null;
            const nw = totalAssets - calcs.totalDebt;
            return <StatCard icon="💼" label="Net Worth" value={nw} color={nw >= 0 ? "#6366f1" : "#ef4444"} />;
          })()}
        </div>

        {calcs.pieData.length > 0 && (
          <div style={cardStyle}>
            <h3 style={{ color: "#e2e8f0", fontSize: 16, marginBottom: 12 }}>Where Your Money Goes</h3>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={calcs.pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={90} dataKey="value">
                  {calcs.pieData.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => fmt(v)} contentStyle={tooltipStyle} />
                <Legend formatter={(v, e) => <span style={legendStyle}>{v}: {fmt(e.payload.value)}</span>} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {calcs.expPie.length > 0 && (
          <div style={{ ...cardStyle, marginTop: 12 }}>
            <h3 style={{ color: "#e2e8f0", fontSize: 16, marginBottom: 12 }}>Expense Breakdown</h3>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={calcs.expPie} cx="50%" cy="50%" innerRadius={45} outerRadius={80} dataKey="value">
                  {calcs.expPie.map((e, i) => <Cell key={i} fill={COLORS[(i + 3) % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => fmt(v)} contentStyle={tooltipStyle} />
                <Legend formatter={(v, e) => <span style={legendStyle}>{v}: {fmt(e.payload.value)}</span>} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {calcs.savingsPie.length > 0 && (
          <div style={{ ...cardStyle, marginTop: 12 }}>
            <h3 style={{ color: "#e2e8f0", fontSize: 16, marginBottom: 12 }}>Savings Allocation</h3>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={calcs.savingsPie} cx="50%" cy="50%" innerRadius={45} outerRadius={80} dataKey="value">
                  {calcs.savingsPie.map((e, i) => <Cell key={i} fill={COLORS[(i + 6) % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => fmt(v)} contentStyle={tooltipStyle} />
                <Legend formatter={(v, e) => <span style={legendStyle}>{v}: {fmt(e.payload.value)}</span>} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        <Modal open={ratesModal} onClose={() => setRatesModal(false)} title="💱 Exchange Rates">
          <ExchangeRateEditor />
        </Modal>
      </div>
    );
  };

  /* ── Projections ── */
  const Projections = () => {
    const [horizon, setHorizon] = useState(24);
    const months = calcs.months.slice(0, horizon);
    const tickInterval = horizon <= 24 ? 2 : horizon <= 36 ? 3 : 5;
    const tooltipStyle = { background: "#1e1e2e", border: "1px solid #334155", borderRadius: 8 };

    // Milestones: loan end dates + lump sum due dates within horizon
    const now = new Date();
    const milestones = [
      ...data.loans.filter(l => l.endDate).map(l => ({ date: l.endDate, label: `${l.name} — loan ends`, icon: "🏦" })),
      ...data.lumpSums.filter(l => l.dueDate).map(l => ({ date: l.dueDate, label: `${l.name} — ${fmtCur(l.amount, l.currency || "EUR")} due`, icon: "🎯" })),
    ]
      .filter(m => {
        const d = new Date(m.date);
        const endD = new Date(now.getFullYear(), now.getMonth() + horizon, 1);
        return d > now && d < endD;
      })
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    return (
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4, flexWrap: "wrap", gap: 8 }}>
          <h2 style={{ color: "#e2e8f0", fontSize: 20, margin: 0 }}>📈 Projections</h2>
          <div style={{ display: "flex", gap: 4 }}>
            {[24, 36, 60].map(h => (
              <button key={h} onClick={() => setHorizon(h)}
                style={{ ...btnOutline, padding: "5px 12px", fontSize: 12, background: horizon === h ? "#6366f1" : "transparent", color: horizon === h ? "white" : "#94a3b8", border: "1px solid " + (horizon === h ? "#6366f1" : "#334155") }}>
                {h}mo
              </button>
            ))}
          </div>
        </div>
        <p style={{ color: "#64748b", fontSize: 12, marginBottom: 16 }}>All values in EUR · {horizon}-month view</p>

        <div style={cardStyle}>
          <h3 style={{ color: "#e2e8f0", fontSize: 15, marginBottom: 12 }}>Income vs Outgoing</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={months}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="label" stroke="#64748b" fontSize={10} interval={tickInterval} />
              <YAxis stroke="#64748b" fontSize={10} tickFormatter={(v) => fmt(v)} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => fmt(v)} />
              <Legend />
              <Bar dataKey="income" fill="#10b981" name="Income" radius={[2, 2, 0, 0]} />
              <Bar dataKey="expenses" fill="#ef4444" name="Expenses" radius={[2, 2, 0, 0]} />
              <Bar dataKey="annual" fill="#f59e0b" name="Annual" radius={[2, 2, 0, 0]} />
              <Bar dataKey="loans" fill="#f97316" name="Loans" radius={[2, 2, 0, 0]} />
              <Bar dataKey="lumpSums" fill="#06b6d4" name="Lump Sums" radius={[2, 2, 0, 0]} />
              <Bar dataKey="savings" fill="#8b5cf6" name="Savings" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={{ ...cardStyle, marginTop: 12 }}>
          <h3 style={{ color: "#e2e8f0", fontSize: 15, marginBottom: 12 }}>Net Cash Flow</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={months}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="label" stroke="#64748b" fontSize={10} interval={tickInterval} />
              <YAxis stroke="#64748b" fontSize={10} tickFormatter={(v) => fmt(v)} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => fmt(v)} />
              <Line type="monotone" dataKey="net" stroke="#10b981" strokeWidth={2} dot={false} name="Net Cash Flow" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div style={{ ...cardStyle, marginTop: 12 }}>
          <h3 style={{ color: "#e2e8f0", fontSize: 15, marginBottom: 12 }}>Cumulative Savings Growth</h3>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={months}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="label" stroke="#64748b" fontSize={10} interval={tickInterval} />
              <YAxis stroke="#64748b" fontSize={10} tickFormatter={(v) => fmt(v)} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => fmt(v)} />
              <Area type="monotone" dataKey="cumSavings" stroke="#6366f1" fill="#6366f133" strokeWidth={2} name="Cumulative Savings" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {milestones.length > 0 && (
          <div style={{ ...cardStyle, marginTop: 12 }}>
            <h3 style={{ color: "#e2e8f0", fontSize: 15, marginBottom: 12 }}>Upcoming Milestones</h3>
            {milestones.map((m, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: i < milestones.length - 1 ? "1px solid #2d2d44" : "none" }}>
                <span style={{ fontSize: 18 }}>{m.icon}</span>
                <div>
                  <div style={{ color: "#e2e8f0", fontSize: 13 }}>{m.label}</div>
                  <div style={{ color: "#64748b", fontSize: 11 }}>{new Date(m.date).toLocaleDateString("en-US", { month: "long", year: "numeric" })}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  /* ── Scenario ── */
  const ScenarioTab = () => {
    const [showItemOverrides, setShowItemOverrides] = useState(false);
    const adj = { expenseMultiplier: 1, savingsMultiplier: 1, incomeMultiplier: 1, startMonth: toMonth(today()), itemOverrides: {}, ...(data.scenarioAdjustments || {}) };
    const update = (field, val) => save({ ...data, scenarioAdjustments: { ...adj, [field]: val } });
    const r = liveRates;
    const eur = (amt, cur) => toEur(amt, cur, r);

    // Scenario column: apply multipliers to baseline (no itemOverrides adjustment here, those are already baked into calcs)
    const baseIncome = data.income.reduce((s, i) => s + eur(i.amount, i.currency || "EUR"), 0);
    const baseExpenses = data.monthlyExpenses.reduce((s, i) => s + eur(i.amount, i.currency || "EUR"), 0);
    const baseAnnual = data.annualCosts.reduce((s, i) => s + eur(i.amount, i.currency || "EUR") / 12, 0);
    const baseSavings = data.savings.reduce((s, i) => s + eur(i.amount, i.currency || "EUR"), 0);

    const itemOverrides = adj.itemOverrides || {};
    const scenIncome = baseIncome * adj.incomeMultiplier;
    const scenExpenses = data.monthlyExpenses.reduce((s, i) => {
      const base = itemOverrides[i.id] !== undefined ? Number(itemOverrides[i.id]) : eur(i.amount, i.currency || "EUR");
      return s + (itemOverrides[i.id] !== undefined ? base : base * adj.expenseMultiplier);
    }, 0);
    const scenSavings = data.savings.reduce((s, i) => {
      const base = itemOverrides[i.id] !== undefined ? Number(itemOverrides[i.id]) : eur(i.amount, i.currency || "EUR");
      return s + (itemOverrides[i.id] !== undefined ? base : base * adj.savingsMultiplier);
    }, 0);
    const scenNet = scenIncome - scenExpenses - baseAnnual - calcs.totalLoans - calcs.totalLumpMonthly - scenSavings;
    const baseNet = baseIncome - baseExpenses - baseAnnual - calcs.totalLoans - calcs.totalLumpMonthly - baseSavings;

    const setItemOverride = (id, val) => {
      update("itemOverrides", { ...itemOverrides, [id]: val === "" ? undefined : Number(val) });
    };
    const clearOverride = (id) => {
      const next = { ...itemOverrides };
      delete next[id];
      update("itemOverrides", next);
    };

    const compRow = (label, base, scen) => {
      const diff = scen - base;
      return (
        <>
          <div style={{ color: "#94a3b8", fontSize: 13 }}>{label}</div>
          <div style={{ textAlign: "right", fontSize: 13 }}>{fmt(base)}</div>
          <div style={{ textAlign: "right", fontSize: 13, color: diff !== 0 ? (diff > 0 ? "#10b981" : "#ef4444") : "#e2e8f0" }}>{fmt(scen)}</div>
        </>
      );
    };

    return (
      <div>
        <h2 style={{ color: "#e2e8f0", fontSize: 20, marginBottom: 8 }}>🔮 What-If Scenario</h2>
        <p style={{ color: "#94a3b8", fontSize: 13, marginBottom: 20, lineHeight: 1.5 }}>
          Adjust multipliers to simulate income changes, spending cuts, or savings increases. Apply from a specific month for phased planning.
        </p>
        <div style={cardStyle}>
          <FormField label={`Income Multiplier: ${(adj.incomeMultiplier * 100).toFixed(0)}%`}>
            <input type="range" min="0.5" max="1.5" step="0.05" value={adj.incomeMultiplier}
              onChange={(e) => update("incomeMultiplier", Number(e.target.value))} style={{ width: "100%" }} />
            <div style={{ display: "flex", justifyContent: "space-between", color: "#64748b", fontSize: 11 }}><span>50%</span><span>100%</span><span>150%</span></div>
          </FormField>
          <FormField label={`Expense Multiplier: ${(adj.expenseMultiplier * 100).toFixed(0)}%`}>
            <input type="range" min="0.5" max="1.5" step="0.05" value={adj.expenseMultiplier}
              onChange={(e) => update("expenseMultiplier", Number(e.target.value))} style={{ width: "100%" }} />
            <div style={{ display: "flex", justifyContent: "space-between", color: "#64748b", fontSize: 11 }}><span>50%</span><span>100%</span><span>150%</span></div>
          </FormField>
          <FormField label={`Savings Multiplier: ${(adj.savingsMultiplier * 100).toFixed(0)}%`}>
            <input type="range" min="0" max="2" step="0.1" value={adj.savingsMultiplier}
              onChange={(e) => update("savingsMultiplier", Number(e.target.value))} style={{ width: "100%" }} />
            <div style={{ display: "flex", justifyContent: "space-between", color: "#64748b", fontSize: 11 }}><span>0%</span><span>100%</span><span>200%</span></div>
          </FormField>
          <FormField label="Apply Changes From">
            <input type="month" style={inputStyle} value={adj.startMonth} onChange={(e) => update("startMonth", e.target.value)} />
          </FormField>
        </div>

        {/* Numerical comparison */}
        <div style={{ ...cardStyle, marginTop: 12 }}>
          <h4 style={{ color: "#e2e8f0", fontSize: 15, marginBottom: 12 }}>Monthly Impact</h4>
          <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto", gap: "6px 16px", alignItems: "center" }}>
            <div />
            <div style={{ color: "#94a3b8", fontWeight: 600, textAlign: "right", fontSize: 12 }}>Baseline</div>
            <div style={{ color: "#6366f1", fontWeight: 600, textAlign: "right", fontSize: 12 }}>Scenario</div>
            {compRow("Income", baseIncome, scenIncome)}
            {compRow("Expenses", baseExpenses, scenExpenses)}
            {compRow("Annual (monthly)", baseAnnual, baseAnnual)}
            {compRow("Loans", calcs.totalLoans, calcs.totalLoans)}
            {compRow("Savings", baseSavings, scenSavings)}
            <div style={{ color: "#e2e8f0", fontWeight: 700, borderTop: "1px solid #334155", paddingTop: 8, fontSize: 13 }}>Net</div>
            <div style={{ textAlign: "right", fontWeight: 700, color: baseNet >= 0 ? "#10b981" : "#ef4444", borderTop: "1px solid #334155", paddingTop: 8, fontSize: 13 }}>{fmt(baseNet)}</div>
            <div style={{ textAlign: "right", fontWeight: 700, color: scenNet >= 0 ? "#10b981" : "#ef4444", borderTop: "1px solid #334155", paddingTop: 8, fontSize: 13 }}>{fmt(scenNet)}</div>
          </div>
        </div>

        {/* Per-item overrides */}
        <div style={{ ...cardStyle, marginTop: 12 }}>
          <button style={{ background: "none", border: "none", color: "#e2e8f0", cursor: "pointer", fontSize: 15, fontWeight: 600, padding: 0, display: "flex", alignItems: "center", gap: 8 }}
            onClick={() => setShowItemOverrides(!showItemOverrides)}>
            {showItemOverrides ? "▼" : "▶"} Override Specific Items
          </button>
          {showItemOverrides && (
            <div style={{ marginTop: 12 }}>
              <p style={{ color: "#64748b", fontSize: 12, marginBottom: 12 }}>Set exact monthly amounts for specific items. Overrides take priority over multipliers.</p>
              {data.monthlyExpenses.length > 0 && (
                <>
                  <div style={{ color: "#6366f1", fontSize: 11, fontWeight: 700, letterSpacing: 1, marginBottom: 6, textTransform: "uppercase" }}>Monthly Expenses</div>
                  {data.monthlyExpenses.map(i => {
                    const base = eur(i.amount, i.currency || "EUR");
                    const hasOverride = itemOverrides[i.id] !== undefined;
                    return (
                      <div key={i.id} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                        <span style={{ flex: 1, color: "#e2e8f0", fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{i.name}</span>
                        <span style={{ color: "#64748b", fontSize: 12, flexShrink: 0 }}>{fmt(base)}</span>
                        <input style={{ ...inputStyle, width: 90, padding: "5px 8px", fontSize: 12 }} type="number" min="0" step="10"
                          placeholder="Override" value={hasOverride ? itemOverrides[i.id] : ""}
                          onChange={e => setItemOverride(i.id, e.target.value)} />
                        {hasOverride && <button onClick={() => clearOverride(i.id)} style={{ ...btnDanger, padding: "4px 8px", fontSize: 11 }}>✕</button>}
                      </div>
                    );
                  })}
                </>
              )}
              {data.savings.length > 0 && (
                <>
                  <div style={{ color: "#8b5cf6", fontSize: 11, fontWeight: 700, letterSpacing: 1, marginBottom: 6, marginTop: 12, textTransform: "uppercase" }}>Savings</div>
                  {data.savings.map(i => {
                    const base = eur(i.amount, i.currency || "EUR");
                    const hasOverride = itemOverrides[i.id] !== undefined;
                    return (
                      <div key={i.id} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                        <span style={{ flex: 1, color: "#e2e8f0", fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{i.name}</span>
                        <span style={{ color: "#64748b", fontSize: 12, flexShrink: 0 }}>{fmt(base)}</span>
                        <input style={{ ...inputStyle, width: 90, padding: "5px 8px", fontSize: 12 }} type="number" min="0" step="10"
                          placeholder="Override" value={hasOverride ? itemOverrides[i.id] : ""}
                          onChange={e => setItemOverride(i.id, e.target.value)} />
                        {hasOverride && <button onClick={() => clearOverride(i.id)} style={{ ...btnDanger, padding: "4px 8px", fontSize: 11 }}>✕</button>}
                      </div>
                    );
                  })}
                </>
              )}
              {Object.keys(itemOverrides).length > 0 && (
                <button style={{ ...btnOutline, fontSize: 12, marginTop: 8 }} onClick={() => update("itemOverrides", {})}>Clear All Overrides</button>
              )}
            </div>
          )}
        </div>

        <div style={{ marginTop: 12 }}>
          <h3 style={{ color: "#e2e8f0", fontSize: 15, marginBottom: 12 }}>Projected Impact (EUR)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={calcs.months.slice(0, 24)}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="label" stroke="#64748b" fontSize={11} interval={2} />
              <YAxis stroke="#64748b" fontSize={11} tickFormatter={(v) => fmt(v)} />
              <Tooltip contentStyle={{ background: "#1e1e2e", border: "1px solid #334155", borderRadius: 8 }} formatter={(v) => fmt(v)} />
              <Area type="monotone" dataKey="net" stroke="#10b981" fill="#10b98133" name="Net Cash Flow" />
              <Area type="monotone" dataKey="cumSavings" stroke="#6366f1" fill="#6366f133" name="Cumulative Savings" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  /* ── Loan Payoff Planner ── */
  const LoanPayoffTab = () => {
    const [newScenarioName, setNewScenarioName] = useState("");
    const [showNewForm, setShowNewForm] = useState(false);
    const [draftOverrides, setDraftOverrides] = useState({});

    const activeScenarioId = activePayoffScenarioId;
    const setActiveScenarioId = setActivePayoffScenarioId;

    // Draft helpers: type freely without triggering Firestore saves (saves happen on blur)
    const getDraft = (loanId, field, fallback) =>
      draftOverrides[loanId]?.[field] !== undefined ? draftOverrides[loanId][field] : (fallback ?? "");
    const setDraft = (loanId, field, value) =>
      setDraftOverrides((prev) => ({ ...prev, [loanId]: { ...(prev[loanId] || {}), [field]: value } }));
    const commitDraft = (loanId, field) => {
      const val = draftOverrides[loanId]?.[field];
      if (val === undefined) return;
      setDraftOverrides((prev) => {
        const next = { ...prev };
        if (next[loanId]) {
          const d = { ...next[loanId] };
          delete d[field];
          if (Object.keys(d).length === 0) delete next[loanId]; else next[loanId] = d;
        }
        return next;
      });
      updateOverride(loanId, field, val !== "" ? Number(val) : null);
    };

    const r = liveRates;
    const scenarios = data.loanPayoffScenarios || [];
    const activeScenario = scenarios.find((s) => s.id === activeScenarioId) || null;
    const activeLoanOverrides = activeScenario?.loanOverrides || {};
    const now = new Date();

    const createScenario = () => {
      if (!newScenarioName.trim()) return;
      const s = { id: uid(), name: newScenarioName.trim(), loanOverrides: {} };
      save({ ...data, loanPayoffScenarios: [...scenarios, s] });
      setActiveScenarioId(s.id);
      setNewScenarioName("");
      setShowNewForm(false);
    };

    const deleteScenario = (id) => {
      save({ ...data, loanPayoffScenarios: scenarios.filter((s) => s.id !== id) });
      if (activeScenarioId === id) setActiveScenarioId(null);
    };

    const updateOverride = (loanId, field, value) => {
      if (!activeScenario) return;
      const updated = scenarios.map((s) => {
        if (s.id !== activeScenarioId) return s;
        return {
          ...s,
          loanOverrides: {
            ...s.loanOverrides,
            [loanId]: { ...(s.loanOverrides[loanId] || {}), [field]: value },
          },
        };
      });
      save({ ...data, loanPayoffScenarios: updated });
    };

    const calcPayoffMonths = (balanceEur, monthlyPaymentEur, annualRatePct) => {
      if (!balanceEur || balanceEur <= 0 || !monthlyPaymentEur || monthlyPaymentEur <= 0) return null;
      if (!annualRatePct || annualRatePct <= 0) return Math.ceil(balanceEur / monthlyPaymentEur);
      const monthlyRate = annualRatePct / 100 / 12;
      const interestComponent = balanceEur * monthlyRate;
      if (monthlyPaymentEur <= interestComponent) return null;
      return Math.ceil(-Math.log(1 - interestComponent / monthlyPaymentEur) / Math.log(1 + monthlyRate));
    };

    const monthsToLabel = (months) => {
      if (months === null || months === undefined) return "N/A";
      const y = Math.floor(months / 12);
      const m = months % 12;
      if (y === 0) return `${m}mo`;
      if (m === 0) return `${y}yr`;
      return `${y}yr ${m}mo`;
    };

    const getLoanInfo = (loan) => {
      const override = activeLoanOverrides[loan.id] || {};
      const minPaymentEur = getLoanMonthlyEur(loan, r);
      const altRaw = override.alternativePayment ? Number(override.alternativePayment) : null;
      const altPaymentEur = altRaw && altRaw >= minPaymentEur ? altRaw : null;
      // Fall back to loan's own interest rate if no scenario override
      const annualRate = override.interestRate !== null && override.interestRate !== undefined
        ? Number(override.interestRate)
        : (loan.interestRate ? Number(loan.interestRate) : 0);

      let balanceEur = 0;
      if (loan.loanType === "payoff") {
        balanceEur = toEur(Number(loan.remainingBalance || 0), loan.currency || "EUR", r);
      } else if (override.remainingBalance) {
        balanceEur = toEur(Number(override.remainingBalance), loan.currency || "EUR", r);
      }

      const monthsRemaining = loan.endDate ? Math.max(1, monthDiff(today(), loan.endDate)) : null;
      const currentPayoffMonths = balanceEur > 0 ? calcPayoffMonths(balanceEur, minPaymentEur, annualRate) : monthsRemaining;
      const altPayoffMonths = altPaymentEur && balanceEur > 0 ? calcPayoffMonths(balanceEur, altPaymentEur, annualRate) : null;

      let currentInterest = null;
      let altInterest = null;
      if (annualRate > 0 && balanceEur > 0) {
        if (currentPayoffMonths) currentInterest = Math.max(0, minPaymentEur * currentPayoffMonths - balanceEur);
        if (altPayoffMonths && altPaymentEur) altInterest = Math.max(0, altPaymentEur * altPayoffMonths - balanceEur);
      }

      const altInputRaw = override.alternativePayment ? Number(override.alternativePayment) : null;
      const altInputBelowMin = altInputRaw !== null && altInputRaw < minPaymentEur;

      return {
        minPaymentEur, altPaymentEur, balanceEur, annualRate,
        currentPayoffMonths, altPayoffMonths, currentInterest, altInterest,
        monthsSaved: currentPayoffMonths && altPayoffMonths ? currentPayoffMonths - altPayoffMonths : null,
        interestSaved: currentInterest !== null && altInterest !== null ? currentInterest - altInterest : null,
        hasBalance: balanceEur > 0,
        hasAlt: altPaymentEur !== null,
        altInputBelowMin,
        altInputRaw,
      };
    };

    const activeLoans = data.loans.filter((l) => !l.endDate || new Date(l.endDate) >= now);

    const buildTimelineData = () => {
      const horizon = 60;
      const bBals = {};
      const sBals = {};
      const fixedEnds = {};
      const bPayoff = {};
      const sPayoff = {};

      activeLoans.forEach((loan) => {
        const info = getLoanInfo(loan);
        if (info.hasBalance) {
          bBals[loan.id] = info.balanceEur;
          sBals[loan.id] = info.balanceEur;
        } else if (loan.endDate) {
          fixedEnds[loan.id] = Math.max(0, monthDiff(today(), loan.endDate));
        }
      });

      const fixedOut = calcs.totalMonthly + calcs.totalAnnualMonthly + calcs.totalLumpMonthly + calcs.totalSavings;
      const income = calcs.totalIncome;
      const points = [];

      for (let m = 0; m < horizon; m++) {
        let bLoan = 0;
        let sLoan = 0;

        activeLoans.forEach((loan) => {
          const info = getLoanInfo(loan);
          const minPay = info.minPaymentEur;
          const altPay = info.altPaymentEur || minPay;
          const mr = info.annualRate > 0 ? info.annualRate / 100 / 12 : 0;

          if (bBals[loan.id] !== undefined) {
            if (bBals[loan.id] > 0) {
              const interest = bBals[loan.id] * mr;
              const pay = Math.min(minPay, bBals[loan.id] + interest);
              bLoan += pay;
              bBals[loan.id] = Math.max(0, bBals[loan.id] - (pay - interest));
              if (bBals[loan.id] === 0 && !bPayoff[loan.id]) bPayoff[loan.id] = m + 1;
            }
            if (sBals[loan.id] > 0) {
              const interest = sBals[loan.id] * mr;
              const pay = Math.min(altPay, sBals[loan.id] + interest);
              sLoan += pay;
              sBals[loan.id] = Math.max(0, sBals[loan.id] - (pay - interest));
              if (sBals[loan.id] === 0 && !sPayoff[loan.id]) sPayoff[loan.id] = m + 1;
            }
          } else if (fixedEnds[loan.id] !== undefined) {
            if (m < fixedEnds[loan.id]) {
              bLoan += minPay;
              sLoan += altPay;
            } else {
              if (!bPayoff[loan.id]) bPayoff[loan.id] = fixedEnds[loan.id];
              if (!sPayoff[loan.id]) sPayoff[loan.id] = fixedEnds[loan.id];
            }
          } else {
            bLoan += minPay;
            sLoan += altPay;
          }
        });

        const mo = m + 1;
        const label = m === 0 ? "Now" : mo % 12 === 0 ? `Yr ${mo / 12}` : mo % 6 === 0 ? `Mo ${mo}` : "";
        points.push({
          m,
          label,
          baseNet: Math.round(income - fixedOut - bLoan),
          scenNet: Math.round(income - fixedOut - sLoan),
          baseSpend: Math.round(fixedOut + bLoan),
          scenSpend: Math.round(fixedOut + sLoan),
        });
      }

      const events = activeLoans
        .filter((loan) => bPayoff[loan.id] || sPayoff[loan.id] || fixedEnds[loan.id] !== undefined)
        .map((loan) => ({
          name: loan.name,
          baseMonth: bPayoff[loan.id] ?? (fixedEnds[loan.id] !== undefined ? fixedEnds[loan.id] : null),
          scenMonth: sPayoff[loan.id] ?? (fixedEnds[loan.id] !== undefined ? fixedEnds[loan.id] : null),
        }));

      return { points, events };
    };

    const baselineLoanTotal = calcs.totalLoans;
    const scenarioLoanTotal = activeLoans.reduce((sum, loan) => {
      const override = activeLoanOverrides[loan.id] || {};
      const min = getLoanMonthlyEur(loan, r);
      const alt = override.alternativePayment ? Number(override.alternativePayment) : null;
      return sum + (alt && alt >= min ? alt : min);
    }, 0);

    const scenarioNet = calcs.netMonthly - (scenarioLoanTotal - baselineLoanTotal);
    const cashFlowChange = scenarioNet - calcs.netMonthly;

    const rowStyle = { display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: "1px solid #2d2d44", fontSize: 13 };

    return (
      <div>
        <h2 style={{ color: "#e2e8f0", fontSize: 20, marginBottom: 4 }}>⚡ Loan Payoff Planner</h2>
        <p style={{ color: "#64748b", fontSize: 12, marginBottom: 16 }}>
          Create scenarios to see how extra loan payments affect your cash flow and payoff dates. All calculations in EUR.
        </p>

        {/* Scenario selector */}
        <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
          {scenarios.map((s) => (
            <button key={s.id} onClick={() => setActiveScenarioId(s.id)}
              style={{
                padding: "8px 16px", borderRadius: 20, border: "1px solid " + (activeScenarioId === s.id ? "#6366f1" : "#2d2d44"),
                cursor: "pointer", fontSize: 13, background: activeScenarioId === s.id ? "#6366f1" : "#1e1e2e",
                color: activeScenarioId === s.id ? "white" : "#94a3b8", fontWeight: activeScenarioId === s.id ? 600 : 400,
              }}>{s.name}</button>
          ))}
          {showNewForm ? (
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <input style={{ ...inputStyle, width: 160, padding: "6px 10px" }} value={newScenarioName}
                onChange={(e) => setNewScenarioName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && createScenario()}
                placeholder="Scenario name" autoFocus />
              <button style={{ ...btnPrimary, padding: "6px 12px", fontSize: 12 }} onClick={createScenario}>Create</button>
              <button style={{ ...btnOutline, padding: "6px 12px", fontSize: 12 }} onClick={() => { setShowNewForm(false); setNewScenarioName(""); }}>Cancel</button>
            </div>
          ) : (
            <button style={{ ...btnOutline, padding: "6px 16px", fontSize: 12 }} onClick={() => setShowNewForm(true)}>+ New Scenario</button>
          )}
        </div>

        {scenarios.length === 0 && (
          <div style={{ ...cardStyle, textAlign: "center", padding: 48, color: "#64748b" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>⚡</div>
            <p style={{ marginBottom: 8, color: "#94a3b8" }}>No scenarios yet.</p>
            <p style={{ fontSize: 12 }}>Create a scenario to explore how extra loan payments can free you from debt faster.</p>
          </div>
        )}

        {scenarios.length > 0 && !activeScenario && (
          <div style={{ ...cardStyle, textAlign: "center", padding: 32, color: "#64748b" }}>
            Select a scenario above to view and edit it.
          </div>
        )}

        {activeScenario && (
          <div>
            {/* Scenario header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h3 style={{ color: "#e2e8f0", fontSize: 17, margin: 0 }}>{activeScenario.name}</h3>
              <button style={{ ...btnDanger, fontSize: 12 }} onClick={() => deleteScenario(activeScenario.id)}>Delete</button>
            </div>

            {/* Cash flow comparison table */}
            <div style={cardStyle}>
              <h4 style={{ color: "#e2e8f0", fontSize: 15, marginBottom: 14 }}>Cash Flow Comparison</h4>
              <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto", gap: "6px 16px", fontSize: 13, alignItems: "center" }}>
                <div />
                <div style={{ color: "#94a3b8", fontWeight: 600, textAlign: "right" }}>Baseline</div>
                <div style={{ color: "#6366f1", fontWeight: 600, textAlign: "right" }}>This Scenario</div>

                <div style={{ color: "#94a3b8" }}>Income</div>
                <div style={{ color: "#10b981", textAlign: "right" }}>{fmt(calcs.totalIncome)}</div>
                <div style={{ color: "#10b981", textAlign: "right" }}>{fmt(calcs.totalIncome)}</div>

                <div style={{ color: "#94a3b8" }}>Monthly Expenses</div>
                <div style={{ textAlign: "right" }}>{fmt(calcs.totalMonthly)}</div>
                <div style={{ textAlign: "right" }}>{fmt(calcs.totalMonthly)}</div>

                <div style={{ color: "#94a3b8" }}>Annual (monthly)</div>
                <div style={{ textAlign: "right" }}>{fmt(calcs.totalAnnualMonthly)}</div>
                <div style={{ textAlign: "right" }}>{fmt(calcs.totalAnnualMonthly)}</div>

                <div style={{ color: "#94a3b8" }}>Loan Payments</div>
                <div style={{ textAlign: "right" }}>{fmt(baselineLoanTotal)}</div>
                <div style={{ textAlign: "right", color: scenarioLoanTotal > baselineLoanTotal ? "#f59e0b" : "#e2e8f0", fontWeight: scenarioLoanTotal > baselineLoanTotal ? 600 : 400 }}>{fmt(scenarioLoanTotal)}</div>

                <div style={{ color: "#94a3b8" }}>Savings</div>
                <div style={{ textAlign: "right" }}>{fmt(calcs.totalSavings)}</div>
                <div style={{ textAlign: "right" }}>{fmt(calcs.totalSavings)}</div>

                {calcs.totalLumpMonthly > 0 && <>
                  <div style={{ color: "#94a3b8" }}>Lump Sums</div>
                  <div style={{ textAlign: "right" }}>{fmt(calcs.totalLumpMonthly)}</div>
                  <div style={{ textAlign: "right" }}>{fmt(calcs.totalLumpMonthly)}</div>
                </>}

                <div style={{ color: "#e2e8f0", fontWeight: 700, borderTop: "1px solid #334155", paddingTop: 8 }}>Net Cash Flow</div>
                <div style={{ textAlign: "right", fontWeight: 700, color: calcs.netMonthly >= 0 ? "#10b981" : "#ef4444", borderTop: "1px solid #334155", paddingTop: 8 }}>{fmt(calcs.netMonthly)}</div>
                <div style={{ textAlign: "right", fontWeight: 700, color: scenarioNet >= 0 ? "#10b981" : "#ef4444", borderTop: "1px solid #334155", paddingTop: 8 }}>{fmt(scenarioNet)}</div>
              </div>

              {cashFlowChange !== 0 && (
                <div style={{ marginTop: 12, padding: "10px 14px", borderRadius: 8, background: "#f59e0b20", border: "1px solid #f59e0b" }}>
                  <span style={{ color: "#f59e0b", fontWeight: 600, fontSize: 14 }}>
                    {fmt(Math.abs(cashFlowChange))}/month redirected to extra loan payments
                  </span>
                  <span style={{ color: "#64748b", fontSize: 12, marginLeft: 8 }}>
                    · Available cash {scenarioNet >= 0 ? fmt(scenarioNet) : fmt(scenarioNet) + " (deficit)"}
                  </span>
                </div>
              )}
            </div>

            {/* Loan adjustments */}
            {activeLoans.length === 0 ? (
              <div style={{ ...cardStyle, textAlign: "center", color: "#64748b", padding: 24, marginTop: 16 }}>
                No active loans. Go to the Loans tab to add your loans.
              </div>
            ) : (
              <>
                <h4 style={{ color: "#94a3b8", fontSize: 14, marginTop: 20, marginBottom: 10 }}>Loan Payment Adjustments</h4>
                {activeLoans.map((loan) => {
                  const info = getLoanInfo(loan);
                  const override = activeLoanOverrides[loan.id] || {};
                  const cur = loan.currency || "EUR";
                  return (
                    <div key={loan.id} style={{ ...cardStyle, borderLeft: "3px solid #6366f1" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                        <div>
                          <div style={{ fontWeight: 600, color: "#e2e8f0", fontSize: 15 }}>{loan.name}</div>
                          {loan.category && <div style={{ color: "#6366f1", fontSize: 12 }}>{loan.category}</div>}
                          {loan.loanType === "payoff" && loan.remainingBalance && (
                            <div style={{ color: "#f59e0b", fontSize: 12 }}>Balance: {fmtCur(loan.remainingBalance, cur)}</div>
                          )}
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ color: "#64748b", fontSize: 11 }}>Min. payment</div>
                          <div style={{ fontWeight: 700, color: "#e2e8f0", fontSize: 16 }}>{fmt(info.minPaymentEur)}<span style={{ fontSize: 11, color: "#94a3b8" }}>/mo</span></div>
                        </div>
                      </div>

                      {/* Interest rate */}
                      <div style={{ marginBottom: 12 }}>
                        <label style={labelStyle}>Annual Interest Rate % <span style={{ color: "#64748b", fontWeight: 400 }}>(optional)</span></label>
                        <input style={{ ...inputStyle }} type="number" min="0" max="100" step="0.01"
                          placeholder="e.g. 5.5 — leave blank if unknown"
                          value={getDraft(loan.id, "interestRate", override.interestRate)}
                          onChange={(e) => setDraft(loan.id, "interestRate", e.target.value)}
                          onBlur={() => commitDraft(loan.id, "interestRate")}
                        />
                        {!info.annualRate && <p style={{ color: "#64748b", fontSize: 11, marginTop: 3 }}>Without a rate, payoff is estimated as balance ÷ payment.</p>}
                      </div>

                      {/* Remaining balance for fixed loans */}
                      {loan.loanType === "fixed" && (
                        <div style={{ marginBottom: 12 }}>
                          <label style={labelStyle}>Remaining Balance ({cur}) <span style={{ color: "#64748b", fontWeight: 400 }}>(optional, needed for payoff timeline)</span></label>
                          <input style={inputStyle} type="number" min="0" step="100"
                            placeholder="Enter to see payoff timeline"
                            value={getDraft(loan.id, "remainingBalance", override.remainingBalance)}
                            onChange={(e) => setDraft(loan.id, "remainingBalance", e.target.value)}
                            onBlur={() => commitDraft(loan.id, "remainingBalance")}
                          />
                        </div>
                      )}

                      {/* Alternative payment */}
                      <div style={{ marginBottom: 12 }}>
                        <label style={labelStyle}>Alternative Monthly Payment (EUR) <span style={{ color: "#64748b", fontWeight: 400 }}>— must be ≥ minimum</span></label>
                        <input
                          style={{ ...inputStyle, borderColor: info.altInputBelowMin ? "#ef4444" : "#334155" }}
                          type="number" min={Math.ceil(info.minPaymentEur)} step="10"
                          placeholder={`Min: ${Math.ceil(info.minPaymentEur)}`}
                          value={getDraft(loan.id, "alternativePayment", override.alternativePayment)}
                          onChange={(e) => setDraft(loan.id, "alternativePayment", e.target.value)}
                          onBlur={() => commitDraft(loan.id, "alternativePayment")}
                        />
                        {info.altInputBelowMin && (
                          <p style={{ color: "#ef4444", fontSize: 11, marginTop: 3 }}>
                            Must be at least {fmt(info.minPaymentEur)} (current minimum payment)
                          </p>
                        )}
                      </div>

                      {/* Payoff timeline comparison */}
                      {info.hasBalance && (
                        <div style={{ background: "#0f172a", borderRadius: 8, padding: 12 }}>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                            <div>
                              <div style={{ color: "#64748b", fontSize: 11, marginBottom: 4 }}>Current payoff</div>
                              <div style={{ color: "#f59e0b", fontWeight: 700, fontSize: 18 }}>{monthsToLabel(info.currentPayoffMonths)}</div>
                              {info.currentInterest !== null && (
                                <div style={{ color: "#64748b", fontSize: 11, marginTop: 2 }}>Total interest: {fmt(info.currentInterest)}</div>
                              )}
                            </div>
                            {info.hasAlt && (
                              <div>
                                <div style={{ color: "#64748b", fontSize: 11, marginBottom: 4 }}>With extra payment</div>
                                {info.altPayoffMonths ? (
                                  <>
                                    <div style={{ color: "#10b981", fontWeight: 700, fontSize: 18 }}>{monthsToLabel(info.altPayoffMonths)}</div>
                                    {info.altInterest !== null && (
                                      <div style={{ color: "#64748b", fontSize: 11, marginTop: 2 }}>Total interest: {fmt(info.altInterest)}</div>
                                    )}
                                  </>
                                ) : (
                                  <div style={{ color: "#ef4444", fontSize: 12 }}>Payment too low to cover interest</div>
                                )}
                              </div>
                            )}
                          </div>
                          {info.monthsSaved !== null && info.monthsSaved > 0 && (
                            <div style={{ marginTop: 10, padding: "8px 12px", borderRadius: 6, background: "#10b98118", border: "1px solid #10b98155" }}>
                              <span style={{ color: "#10b981", fontWeight: 600, fontSize: 13 }}>
                                ⚡ Pay off {monthsToLabel(info.monthsSaved)} sooner
                                {info.interestSaved !== null && info.interestSaved > 0 ? ` · Save ${fmt(info.interestSaved)} in interest` : ""}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                      {!info.hasBalance && loan.loanType === "fixed" && (
                        <p style={{ color: "#64748b", fontSize: 12, fontStyle: "italic" }}>Enter remaining balance above to see payoff timeline.</p>
                      )}
                    </div>
                  );
                })}
              </>
            )}

            {/* Cashflow timeline chart */}
            {activeLoans.length > 0 && (() => {
              const { points, events } = buildTimelineData();
              const hasAnyAlt = activeLoans.some((l) => {
                const ov = activeLoanOverrides[l.id] || {};
                return ov.alternativePayment && Number(ov.alternativePayment) > getLoanMonthlyEur(l, r);
              });
              const sectionStyle = { color: "#94a3b8", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 };
              return (
                <div style={{ ...cardStyle, marginTop: 16 }}>
                  <h4 style={{ color: "#e2e8f0", fontSize: 15, marginBottom: 4 }}>📈 Cashflow & Spending Timeline</h4>
                  <p style={{ color: "#64748b", fontSize: 12, marginBottom: 16 }}>
                    60-month projection showing how your cashflow and spending evolve as loans get paid off.
                    {!hasAnyAlt && <span style={{ color: "#f59e0b" }}> Set alternative payments above to see the scenario line diverge.</span>}
                  </p>

                  <div style={sectionStyle}>Net Cashflow / Month</div>
                  <ResponsiveContainer width="100%" height={200}>
                    <AreaChart data={points}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                      <XAxis dataKey="label" stroke="#64748b" fontSize={10} interval={0} />
                      <YAxis stroke="#64748b" fontSize={10} tickFormatter={(v) => fmt(v)} width={70} />
                      <Tooltip contentStyle={{ background: "#1e1e2e", border: "1px solid #334155", borderRadius: 8 }} formatter={(v) => [fmt(v)]} />
                      <Legend iconType="line" wrapperStyle={{ fontSize: 11 }} />
                      <Area type="monotone" dataKey="baseNet" stroke="#64748b" fill="#64748b18" name="Baseline" dot={false} strokeDasharray="5 3" strokeWidth={1.5} />
                      <Area type="monotone" dataKey="scenNet" stroke="#10b981" fill="#10b98122" name="Scenario" dot={false} strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>

                  <div style={{ ...sectionStyle, marginTop: 20 }}>Total Spending / Month</div>
                  <ResponsiveContainer width="100%" height={200}>
                    <AreaChart data={points}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                      <XAxis dataKey="label" stroke="#64748b" fontSize={10} interval={0} />
                      <YAxis stroke="#64748b" fontSize={10} tickFormatter={(v) => fmt(v)} width={70} />
                      <Tooltip contentStyle={{ background: "#1e1e2e", border: "1px solid #334155", borderRadius: 8 }} formatter={(v) => [fmt(v)]} />
                      <Legend iconType="line" wrapperStyle={{ fontSize: 11 }} />
                      <Area type="monotone" dataKey="baseSpend" stroke="#ef4444" fill="#ef444418" name="Baseline" dot={false} strokeDasharray="5 3" strokeWidth={1.5} />
                      <Area type="monotone" dataKey="scenSpend" stroke="#f59e0b" fill="#f59e0b18" name="Scenario" dot={false} strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>

                  {events.length > 0 && (
                    <div style={{ marginTop: 20 }}>
                      <div style={sectionStyle}>Loan Payoff Schedule</div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto", gap: "6px 14px", fontSize: 12, alignItems: "center" }}>
                        <div style={{ color: "#64748b", fontSize: 11 }}>Loan</div>
                        <div style={{ color: "#64748b", fontSize: 11, textAlign: "right" }}>Baseline</div>
                        <div style={{ color: "#10b981", fontSize: 11, textAlign: "right" }}>Scenario</div>
                        {events.map((ev) => {
                          const saved = ev.baseMonth && ev.scenMonth && ev.scenMonth < ev.baseMonth ? ev.baseMonth - ev.scenMonth : 0;
                          return (
                            <React.Fragment key={ev.name}>
                              <div style={{ color: "#e2e8f0" }}>{ev.name}</div>
                              <div style={{ color: "#94a3b8", textAlign: "right" }}>{ev.baseMonth ? monthsToLabel(ev.baseMonth) : "60+ mo"}</div>
                              <div style={{ textAlign: "right" }}>
                                <span style={{ color: saved > 0 ? "#10b981" : "#94a3b8", fontWeight: saved > 0 ? 600 : 400 }}>
                                  {ev.scenMonth ? monthsToLabel(ev.scenMonth) : "60+ mo"}
                                </span>
                                {saved > 0 && <span style={{ color: "#6366f1", marginLeft: 6, fontSize: 11 }}>({monthsToLabel(saved)} sooner)</span>}
                              </div>
                            </React.Fragment>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Full monthly budget snapshot */}
            <div style={{ ...cardStyle, marginTop: 16 }}>
              <h4 style={{ color: "#e2e8f0", fontSize: 15, marginBottom: 4 }}>Full Monthly Budget Snapshot</h4>
              <p style={{ color: "#64748b", fontSize: 12, marginBottom: 14 }}>Your complete monthly picture in this scenario (all in EUR).</p>

              {data.monthlyExpenses.length > 0 && (
                <>
                  <div style={{ color: "#6366f1", fontSize: 11, fontWeight: 700, letterSpacing: 1, marginBottom: 4, marginTop: 8, textTransform: "uppercase" }}>Monthly Expenses</div>
                  {data.monthlyExpenses.map((e) => (
                    <div key={e.id} style={rowStyle}>
                      <span style={{ color: "#e2e8f0" }}>{e.name}</span>
                      <span style={{ color: "#94a3b8" }}>{fmt(toEur(Number(e.amount || 0), e.currency || "EUR", r))}</span>
                    </div>
                  ))}
                </>
              )}

              {data.annualCosts.length > 0 && (
                <>
                  <div style={{ color: "#f59e0b", fontSize: 11, fontWeight: 700, letterSpacing: 1, marginBottom: 4, marginTop: 14, textTransform: "uppercase" }}>Annual Costs (monthly)</div>
                  {data.annualCosts.map((e) => (
                    <div key={e.id} style={rowStyle}>
                      <span style={{ color: "#e2e8f0" }}>{e.name}</span>
                      <span style={{ color: "#94a3b8" }}>{fmt(toEur(Number(e.amount || 0), e.currency || "EUR", r) / 12)}</span>
                    </div>
                  ))}
                </>
              )}

              {activeLoans.length > 0 && (
                <>
                  <div style={{ color: "#10b981", fontSize: 11, fontWeight: 700, letterSpacing: 1, marginBottom: 4, marginTop: 14, textTransform: "uppercase" }}>Loan Payments</div>
                  {activeLoans.map((l) => {
                    const min = getLoanMonthlyEur(l, r);
                    const override = activeLoanOverrides[l.id] || {};
                    const alt = override.alternativePayment ? Number(override.alternativePayment) : null;
                    const effective = alt && alt >= min ? alt : min;
                    const isExtra = effective > min;
                    return (
                      <div key={l.id} style={rowStyle}>
                        <span style={{ color: "#e2e8f0" }}>{l.name}</span>
                        <span>
                          {isExtra ? (
                            <>
                              <span style={{ color: "#64748b", textDecoration: "line-through", marginRight: 8, fontSize: 12 }}>{fmt(min)}</span>
                              <span style={{ color: "#f59e0b", fontWeight: 600 }}>{fmt(effective)}</span>
                            </>
                          ) : (
                            <span style={{ color: "#94a3b8" }}>{fmt(effective)}</span>
                          )}
                        </span>
                      </div>
                    );
                  })}
                </>
              )}

              {data.savings.length > 0 && (
                <>
                  <div style={{ color: "#8b5cf6", fontSize: 11, fontWeight: 700, letterSpacing: 1, marginBottom: 4, marginTop: 14, textTransform: "uppercase" }}>Savings</div>
                  {data.savings.map((e) => (
                    <div key={e.id} style={rowStyle}>
                      <span style={{ color: "#e2e8f0" }}>{e.name}</span>
                      <span style={{ color: "#94a3b8" }}>{fmt(toEur(Number(e.amount || 0), e.currency || "EUR", r))}</span>
                    </div>
                  ))}
                </>
              )}

              <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0 0", marginTop: 10, fontWeight: 700, fontSize: 14, borderTop: "1px solid #334155" }}>
                <span style={{ color: "#e2e8f0" }}>Total Monthly Out</span>
                <span style={{ color: "#ef4444" }}>{fmt(calcs.totalMonthly + calcs.totalAnnualMonthly + scenarioLoanTotal + calcs.totalLumpMonthly + calcs.totalSavings)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0 0", fontWeight: 700, fontSize: 15 }}>
                <span style={{ color: "#e2e8f0" }}>Net Cash Flow</span>
                <span style={{ color: scenarioNet >= 0 ? "#10b981" : "#ef4444" }}>{fmt(scenarioNet)}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  /* ── Actuals Tab ── */
  const ActualsTab = () => {
    const [actMonth, setActMonth] = useState(toMonth(today()));
    const [showForm, setShowForm] = useState(false);
    const [desc, setDesc] = useState("");
    const [cat, setCat] = useState(EXPENSE_CATEGORIES[0]);
    const [amount, setAmount] = useState("");
    const [currency, setCurrency] = useState("EUR");
    const [errors, setErrors] = useState({});

    const actuals = (data.actuals || []).filter(a => a.month === actMonth);
    const r = liveRates;

    const prevMonth = () => {
      const d = new Date(actMonth + "-01");
      d.setMonth(d.getMonth() - 1);
      setActMonth(toMonth(d.toISOString()));
    };
    const nextMonth = () => {
      const d = new Date(actMonth + "-01");
      d.setMonth(d.getMonth() + 1);
      setActMonth(toMonth(d.toISOString()));
    };

    const totalLogged = actuals.reduce((s, a) => s + toEur(a.amount, a.currency || "EUR", r), 0);
    const budgetedExpenses = calcs.totalMonthly + calcs.totalAnnualMonthly;

    const saveActual = () => {
      const e = {};
      if (!desc.trim()) e.desc = "Description is required";
      if (!amount || Number(amount) <= 0) e.amount = "Amount must be greater than 0";
      if (Object.keys(e).length) { setErrors(e); return; }
      const d = { ...data, actuals: [...(data.actuals || []), { id: uid(), month: actMonth, description: desc, category: cat, amount: Number(amount), currency }] };
      save(d);
      setDesc(""); setAmount(""); setCurrency("EUR"); setShowForm(false); setErrors({});
    };

    const deleteActual = (id) => {
      if (!window.confirm("Delete this entry?")) return;
      save({ ...data, actuals: (data.actuals || []).filter(a => a.id !== id) });
    };

    const monthLabel = new Date(actMonth + "-01").toLocaleDateString("en-US", { month: "long", year: "numeric" });
    const byCategory = {};
    actuals.forEach(a => {
      byCategory[a.category] = (byCategory[a.category] || 0) + toEur(a.amount, a.currency || "EUR", r);
    });

    return (
      <div>
        <h2 style={{ color: "#e2e8f0", fontSize: 20, marginBottom: 8 }}>🧾 Actual Spending</h2>
        <p style={{ color: "#64748b", fontSize: 12, marginBottom: 16 }}>Log actual spending to compare against your budget.</p>

        {/* Month navigator */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <button onClick={prevMonth} style={{ ...btnOutline, padding: "6px 14px" }}>‹</button>
          <span style={{ color: "#e2e8f0", fontWeight: 600, fontSize: 16, flex: 1, textAlign: "center" }}>{monthLabel}</span>
          <button onClick={nextMonth} style={{ ...btnOutline, padding: "6px 14px" }} disabled={actMonth >= toMonth(today())}>›</button>
        </div>

        {/* Summary */}
        <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
          <StatCard icon="🧾" label="Total Logged" value={totalLogged} color="#f59e0b" />
          <StatCard icon="📋" label="Budgeted" value={budgetedExpenses} color="#6366f1" />
          <StatCard icon="📊" label="Difference" value={budgetedExpenses - totalLogged} color={budgetedExpenses >= totalLogged ? "#10b981" : "#ef4444"} />
        </div>

        {/* Category breakdown */}
        {Object.keys(byCategory).length > 0 && (
          <div style={{ ...cardStyle, marginBottom: 16 }}>
            <h4 style={{ color: "#e2e8f0", fontSize: 14, marginBottom: 10 }}>By Category</h4>
            {Object.entries(byCategory).sort((a, b) => b[1] - a[1]).map(([cat, amt]) => (
              <div key={cat} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 13 }}>
                <span style={{ color: "#94a3b8" }}>{cat}</span>
                <span style={{ color: "#e2e8f0" }}>{fmt(amt)}</span>
              </div>
            ))}
          </div>
        )}

        {/* Add entry */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h4 style={{ color: "#e2e8f0", fontSize: 15, margin: 0 }}>Entries</h4>
          <button style={btnPrimary} onClick={() => setShowForm(!showForm)}>+ Log Expense</button>
        </div>
        {showForm && (
          <div style={{ ...cardStyle, borderLeft: "3px solid #6366f1" }}>
            <FormField label="Description">
              <input style={{ ...inputStyle, borderColor: errors.desc ? "#ef4444" : "#334155" }} value={desc} onChange={e => setDesc(e.target.value)} placeholder="e.g. Grocery run" />
              {errors.desc && <p style={errStyle}>{errors.desc}</p>}
            </FormField>
            <FormField label="Category"><Select value={cat} onChange={setCat} options={EXPENSE_CATEGORIES} /></FormField>
            <AmountWithCurrency amount={amount} setAmount={setAmount} currency={currency} setCurrency={setCurrency} label="Amount" rates={r} />
            {errors.amount && <p style={errStyle}>{errors.amount}</p>}
            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              <button style={btnPrimary} onClick={saveActual}>Save</button>
              <button style={btnOutline} onClick={() => { setShowForm(false); setErrors({}); }}>Cancel</button>
            </div>
          </div>
        )}

        {actuals.length === 0 && !showForm && (
          <div style={{ ...cardStyle, textAlign: "center", color: "#64748b", padding: 32 }}>No entries for {monthLabel}. Click "+ Log Expense" to start tracking.</div>
        )}
        {actuals.sort((a, b) => (a.category > b.category ? 1 : -1)).map(a => (
          <div key={a.id} style={{ ...cardStyle, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontWeight: 600, color: "#e2e8f0", fontSize: 14 }}>{a.description}</div>
              <div style={{ color: "#6366f1", fontSize: 12 }}>{a.category}</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ color: "#e2e8f0", fontWeight: 700 }}>{fmtCur(a.amount, a.currency || "EUR")}</div>
              {(a.currency || "EUR") !== "EUR" && <div style={{ color: "#64748b", fontSize: 11 }}>≈ {fmt(toEur(a.amount, a.currency || "EUR", r))}</div>}
              <button onClick={() => deleteActual(a.id)} style={{ ...btnDanger, padding: "3px 8px", fontSize: 11, marginTop: 4 }}>Del</button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  /* ── Accounts & Net Worth ── */
  const AccountsTab = () => {
    const r = liveRates;
    const accounts = data.accounts || [];
    const now = new Date();

    // Asset totals by liquidity
    const byLiquidity = { liquid: 0, "semi-liquid": 0, locked: 0 };
    accounts.forEach(a => {
      const eur = toEur(a.balance, a.currency || "EUR", r);
      byLiquidity[a.liquidity] = (byLiquidity[a.liquidity] || 0) + eur;
    });
    const totalAssets = Object.values(byLiquidity).reduce((s, v) => s + v, 0);

    // Short-term debt: active loans in short-term categories
    const shortTermLoans = data.loans.filter(l =>
      SHORT_TERM_LOAN_CATEGORIES.includes(l.category) &&
      (!l.endDate || new Date(l.endDate) >= now)
    );
    const shortTermDebt = shortTermLoans.reduce((s, l) => {
      if (l.loanType === "payoff") return s + toEur(l.remainingBalance, l.currency || "EUR", r);
      if (l.endDate) return s + toEur(l.amount, l.currency || "EUR", r) * Math.max(0, monthDiff(today(), l.endDate));
      return s;
    }, 0);

    // Long-term debt
    const longTermDebt = calcs.totalDebt - shortTermDebt;

    const totalDebt = calcs.totalDebt;
    const netWorth = totalAssets - totalDebt;

    const liquidAvailable = byLiquidity["liquid"];
    const liquidAndSemi = byLiquidity["liquid"] + byLiquidity["semi-liquid"];
    const canPayLiquidOnly = liquidAvailable >= shortTermDebt;
    const canPayLiquidAndSemi = liquidAndSemi >= shortTermDebt;

    return (
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, flexWrap: "wrap", gap: 8 }}>
          <h2 style={{ color: "#e2e8f0", fontSize: 20, margin: 0 }}>💼 Accounts & Net Worth</h2>
          <button style={btnPrimary} onClick={() => { setEditItem(null); setModal("accounts"); }}>+ Add Account</button>
        </div>
        <p style={{ color: "#64748b", fontSize: 12, marginBottom: 16 }}>All values in EUR. Track your assets and see your true financial position.</p>

        {/* Net Worth Summary */}
        <div style={{ ...cardStyle, background: "linear-gradient(135deg, #1e1e2e 0%, #0f172a 100%)", border: "1px solid #6366f155" }}>
          <h3 style={{ color: "#e2e8f0", fontSize: 16, marginBottom: 16 }}>Net Worth</h3>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
            <div style={{ flex: "1 1 140px", textAlign: "center" }}>
              <div style={{ color: "#64748b", fontSize: 11, marginBottom: 4 }}>Total Assets</div>
              <div style={{ color: "#10b981", fontWeight: 700, fontSize: 22 }}>{fmt(totalAssets)}</div>
            </div>
            <div style={{ flex: "1 1 140px", textAlign: "center" }}>
              <div style={{ color: "#64748b", fontSize: 11, marginBottom: 4 }}>Total Debt (est.)</div>
              <div style={{ color: "#ef4444", fontWeight: 700, fontSize: 22 }}>{fmt(totalDebt)}</div>
            </div>
            <div style={{ flex: "1 1 140px", textAlign: "center" }}>
              <div style={{ color: "#64748b", fontSize: 11, marginBottom: 4 }}>Net Worth</div>
              <div style={{ color: netWorth >= 0 ? "#6366f1" : "#ef4444", fontWeight: 700, fontSize: 22 }}>{fmt(netWorth)}</div>
            </div>
          </div>

          {/* Asset breakdown by liquidity */}
          {totalAssets > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {LIQUIDITY_TYPES.map(l => {
                const val = byLiquidity[l.value] || 0;
                const pct = totalAssets > 0 ? (val / totalAssets * 100) : 0;
                return (
                  <div key={l.value}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, fontSize: 12 }}>
                      <span style={{ color: l.color }}>{l.icon} {l.label}</span>
                      <span style={{ color: "#e2e8f0" }}>{fmt(val)} <span style={{ color: "#64748b" }}>({pct.toFixed(0)}%)</span></span>
                    </div>
                    <div style={{ height: 6, borderRadius: 3, background: "#0f172a", overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${pct}%`, background: l.color, borderRadius: 3 }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Debt breakdown */}
          {totalDebt > 0 && (
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #2d2d44" }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                <span style={{ color: "#f97316" }}>Short-term debt <span style={{ color: "#64748b" }}>(CC, Personal, Student)</span></span>
                <span style={{ color: "#e2e8f0" }}>{fmt(shortTermDebt)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                <span style={{ color: "#64748b" }}>Long-term debt <span style={{ color: "#475569" }}>(Mortgage, Car)</span></span>
                <span style={{ color: "#e2e8f0" }}>{fmt(longTermDebt)}</span>
              </div>
            </div>
          )}
        </div>

        {/* Debt payoff feasibility */}
        {shortTermDebt > 0 && (
          <div style={{ ...cardStyle, marginTop: 4 }}>
            <h4 style={{ color: "#e2e8f0", fontSize: 15, marginBottom: 12 }}>Can You Pay Off Short-Term Debt Now?</h4>
            <p style={{ color: "#64748b", fontSize: 12, marginBottom: 14 }}>
              Short-term debt: <span style={{ color: "#f97316", fontWeight: 600 }}>{fmt(shortTermDebt)}</span>
              {shortTermLoans.length > 0 && (
                <span style={{ color: "#475569" }}> ({shortTermLoans.map(l => l.name).join(", ")})</span>
              )}
            </p>

            {/* Liquid only */}
            <div style={{ padding: "12px 14px", borderRadius: 8, marginBottom: 8, background: canPayLiquidOnly ? "#10b98118" : "#ef444418", border: "1px solid " + (canPayLiquidOnly ? "#10b981" : "#ef4444") }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ color: canPayLiquidOnly ? "#10b981" : "#ef4444", fontWeight: 600, fontSize: 13 }}>
                    {canPayLiquidOnly ? "✓" : "✗"} Using liquid cash only
                  </div>
                  <div style={{ color: "#64748b", fontSize: 11, marginTop: 2 }}>
                    💵 {fmt(liquidAvailable)} available
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  {canPayLiquidOnly ? (
                    <div style={{ color: "#10b981", fontWeight: 700 }}>{fmt(liquidAvailable - shortTermDebt)} left over</div>
                  ) : (
                    <div style={{ color: "#ef4444", fontWeight: 700 }}>{fmt(shortTermDebt - liquidAvailable)} short</div>
                  )}
                </div>
              </div>
            </div>

            {/* Liquid + semi-liquid */}
            {!canPayLiquidOnly && (
              <div style={{ padding: "12px 14px", borderRadius: 8, background: canPayLiquidAndSemi ? "#f59e0b18" : "#ef444418", border: "1px solid " + (canPayLiquidAndSemi ? "#f59e0b" : "#ef4444") }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ color: canPayLiquidAndSemi ? "#f59e0b" : "#ef4444", fontWeight: 600, fontSize: 13 }}>
                      {canPayLiquidAndSemi ? "✓" : "✗"} Using liquid + semi-liquid
                    </div>
                    <div style={{ color: "#64748b", fontSize: 11, marginTop: 2 }}>
                      💵📈 {fmt(liquidAndSemi)} available (may require selling investments)
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    {canPayLiquidAndSemi ? (
                      <div style={{ color: "#f59e0b", fontWeight: 700 }}>{fmt(liquidAndSemi - shortTermDebt)} left over</div>
                    ) : (
                      <div style={{ color: "#ef4444", fontWeight: 700 }}>{fmt(shortTermDebt - liquidAndSemi)} short</div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Account list */}
        <div style={{ marginTop: 16 }}>
          {accounts.length === 0 ? (
            <div style={{ ...cardStyle, textAlign: "center", color: "#64748b", padding: 32 }}>
              No accounts yet. Click "+ Add Account" to start tracking your assets.
            </div>
          ) : (
            // Group by liquidity
            LIQUIDITY_TYPES.map(liq => {
              const group = accounts.filter(a => a.liquidity === liq.value);
              if (group.length === 0) return null;
              const groupTotal = group.reduce((s, a) => s + toEur(a.balance, a.currency || "EUR", r), 0);
              return (
                <div key={liq.value} style={{ marginBottom: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, paddingLeft: 4 }}>
                    <span style={{ color: liq.color, fontSize: 13, fontWeight: 700 }}>{liq.icon} {liq.label}</span>
                    <span style={{ color: "#94a3b8", fontSize: 12 }}>{fmt(groupTotal)}</span>
                  </div>
                  {group.map(a => {
                    const balEur = toEur(a.balance, a.currency || "EUR", r);
                    const cur = a.currency || "EUR";
                    const typeInfo = ACCOUNT_TYPES.find(t => t.value === a.accountType);
                    return (
                      <div key={a.id} style={{ ...cardStyle, borderLeft: `3px solid ${liq.color}` }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, color: "#e2e8f0", fontSize: 15 }}>
                              {a.institution ? `${a.institution} — ` : ""}{a.name}
                            </div>
                            <div style={{ color: "#64748b", fontSize: 12, marginTop: 2 }}>{typeInfo?.label || a.accountType}</div>
                            {a.notes && <div style={{ color: "#64748b", fontSize: 11, marginTop: 3, fontStyle: "italic" }}>{a.notes}</div>}
                          </div>
                          <div style={{ textAlign: "right", flexShrink: 0 }}>
                            <div style={{ fontWeight: 700, color: "#e2e8f0", fontSize: 16 }}>{fmtCur(a.balance, cur)}</div>
                            {cur !== "EUR" && <div style={{ color: "#64748b", fontSize: 11 }}>≈ {fmt(balEur)}</div>}
                            <div style={{ display: "flex", gap: 6, marginTop: 6, justifyContent: "flex-end" }}>
                              <button onClick={() => { setEditItem(a); setModal("accounts"); }} style={{ ...btnOutline, padding: "4px 10px", fontSize: 11 }}>Edit</button>
                              <button onClick={() => deleteItem("accounts", a.id)} style={{ ...btnDanger, padding: "4px 10px", fontSize: 11 }}>Del</button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>

        <Modal open={modal === "accounts"} onClose={() => { setModal(null); setEditItem(null); }} title={editItem ? "Edit Account" : "Add Account"}>
          <AccountForm item={editItem} rates={liveRates} onSave={item => editItem?.id ? updateItem("accounts", item) : addItem("accounts", item)} />
        </Modal>
      </div>
    );
  };

  /* ── Tab Router ── */
  const renderTab = () => {
    switch (tab) {
      case "dashboard": return <Dashboard />;
      case "income": return renderList("income", IncomeForm, "Income Sources");
      case "expenses": return renderList("monthlyExpenses", ExpenseForm, "Monthly Expenses");
      case "annual": return renderList("annualCosts", AnnualForm, "Annual Costs", (i) => {
        const eurAmt = toEur(Number(i.amount), i.currency || "EUR", liveRates);
        const parts = [`Monthly set-aside: ${fmt(eurAmt / 12)}`];
        if (i.dueMonth) parts.push(`Due: ${MONTHS_LIST[i.dueMonth - 1]}`);
        return parts.join(" · ");
      });
      case "loans": return renderList("loans", LoanForm, "Loan Payments", (i) => {
        const cur = i.currency || "EUR";
        if (i.loanType === "payoff") {
          const m = Math.max(1, monthDiff(today(), i.endDate));
          const monthly = getLoanMonthly(i);
          return `Payoff: ${fmtCur(i.remainingBalance, cur)} by ${new Date(i.endDate).toLocaleDateString()} — ${fmtCur(monthly, cur)}/mo (${m} months left)`;
        }
        return i.endDate ? `Ends: ${new Date(i.endDate).toLocaleDateString()}` : "";
      }, ItemCard);
      case "lumpsums": return renderList("lumpSums", LumpSumForm, "Lump Sums", (i) => {
        const m = Math.max(1, monthDiff(today(), i.dueDate));
        const eurAmt = toEur(Number(i.amount), i.currency || "EUR", liveRates);
        return `Due: ${new Date(i.dueDate).toLocaleDateString()} — ${fmt(eurAmt / m)}/mo for ${m} months`;
      });
      case "savings": return renderList("savings", SavingsForm, "Savings");
      case "projections": return <Projections />;
      case "scenario": return <ScenarioTab />;
      case "loanpayoff": return <LoanPayoffTab />;
      case "actuals": return <ActualsTab />;
      case "accounts": return <AccountsTab />;
      default: return <Dashboard />;
    }
  };

  /* ── Loading Screen ── */
  if (authLoading) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#0f0f1a", color: "#e2e8f0" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>💰</div>
        <p style={{ fontSize: 16, color: "#94a3b8" }}>Loading...</p>
      </div>
    </div>
  );

  /* ── Sign In Screen ── */
  if (!user) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "linear-gradient(135deg,#0f0f1a 0%,#1a1a2e 100%)", color: "#e2e8f0", padding: 20 }}>
      <div style={{ textAlign: "center", maxWidth: 400 }}>
        <div style={{ fontSize: 60, marginBottom: 16 }}>💰</div>
        <h1 style={{ fontSize: 28, marginBottom: 8, fontWeight: 700 }}>Budget Planner Pro</h1>
        <p style={{ color: "#94a3b8", marginBottom: 32, lineHeight: 1.6 }}>Take control of your finances. Track expenses, loans, savings goals, and see your money grow.</p>
        <button onClick={signIn} style={{ ...btnPrimary, padding: "14px 32px", fontSize: 16, display: "flex", alignItems: "center", gap: 10, margin: "0 auto" }}>
          <span>🔐</span> Sign in with Google
        </button>
      </div>
    </div>
  );

  /* ── Main Layout ── */
  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "#0f0f1a", color: "#e2e8f0", fontFamily: "-apple-system,BlinkMacSystemFont,Segoe UI,sans-serif", position: "relative" }}>
      {/* Top Bar */}
      <div style={{ position: "fixed", top: 0, left: 0, right: 0, height: 56, background: "#1e1e2e", borderBottom: "1px solid #2d2d44", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 16px", zIndex: 900 }}>
        <button onClick={() => setSidebarOpen(!sidebarOpen)} style={{ background: "none", border: "none", color: "#e2e8f0", fontSize: 24, cursor: "pointer", padding: 4 }}>☰</button>
        <span style={{ fontWeight: 700, fontSize: 16 }}>💰 Budget Planner</span>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {saving && <span style={{ color: "#6366f1", fontSize: 11 }}>Saving...</span>}
          <button onClick={handleSignOut} style={{ ...btnOutline, padding: "4px 10px", fontSize: 11 }}>Sign Out</button>
        </div>
      </div>

      {/* Sidebar Overlay */}
      {sidebarOpen && <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 950 }} onClick={() => setSidebarOpen(false)} />}

      {/* Sidebar */}
      <div style={{
        position: "fixed", top: 56, bottom: 0, left: 0, width: 220, background: "#1e1e2e", borderRight: "1px solid #2d2d44",
        overflowY: "auto", zIndex: 960, transition: "transform 0.2s ease, visibility 0.2s ease",
        transform: sidebarOpen ? "translateX(0)" : "translateX(-100%)",
        visibility: sidebarOpen ? "visible" : "hidden"
      }}>
        <div style={{ padding: 8 }}>
          {tabs.map((t) => (
            <button key={t.id} onClick={() => { setTab(t.id); setSidebarOpen(false); }}
              style={{
                display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "12px 14px", border: "none", borderRadius: 8,
                background: tab === t.id ? "#6366f1" : "transparent", color: tab === t.id ? "white" : "#94a3b8",
                fontSize: 14, cursor: "pointer", marginBottom: 2, textAlign: "left", fontWeight: tab === t.id ? 600 : 400
              }}>
              <span style={{ fontSize: 18 }}>{t.icon}</span>{t.label}
            </button>
          ))}
        </div>
        <div style={{ padding: 16, borderTop: "1px solid #2d2d44", marginTop: 8 }}>
          <div style={{ color: "#64748b", fontSize: 11, marginBottom: 4 }}>Signed in as</div>
          <div style={{ color: "#94a3b8", fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user.email}</div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ flex: 1, marginTop: 56, padding: 16, maxWidth: 800, margin: "56px auto 0", width: "100%", boxSizing: "border-box", paddingBottom: 80 }}>
        {renderTab()}
      </div>

      {/* Bottom Tab Bar — scrollable, all tabs */}
      <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, height: 60, background: "#1e1e2e", borderTop: "1px solid #2d2d44", zIndex: 900, paddingBottom: "env(safe-area-inset-bottom)" }}>
        <div className="hide-scrollbar" style={{ display: "flex", overflowX: "auto", height: "100%", alignItems: "center", padding: "0 4px" }}>
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              display: "flex", flexDirection: "column", alignItems: "center", background: "none", border: "none",
              color: tab === t.id ? "#6366f1" : "#64748b", cursor: "pointer", padding: "4px 10px", fontSize: 10, gap: 2,
              flexShrink: 0, borderBottom: tab === t.id ? "2px solid #6366f1" : "2px solid transparent",
            }}>
              <span style={{ fontSize: 18 }}>{t.icon}</span>
              <span style={{ whiteSpace: "nowrap" }}>{t.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
